// Code generated by tygo. DO NOT EDIT.

//////////
// source: aggregates.go

/**
 * Leaderboard
 */
export interface Leaderboard {
  /**
   * Block/timestamp of the latest leaderboard update
   */
  at: BlockTimestamp;
  /**
   * Leaderboard data, sorted according to requested order
   */
  d: LeaderboardEntry[];
}
/**
 * Leaderboard entry
 */
export interface LeaderboardEntry {
  /**
   * Rank (1-based)
   */
  i: number /* uint32 */;
  /**
   * Wallet address
   */
  a: string;
  /**
   * Total PnL over the leaderboard period, in collateral token: realized PnL
   * net of fees, plus — on a competition leaderboard — the unrealized PnL of
   * the positions still open, marked live until the competition ends and at
   * its cutoff afterwards
   */
  p: Amount;
  /**
   * Total RoI over the leaderboard period, in bps (PnL/Collateral)
   */
  r: number /* int32 */;
  /**
   * Total volume over the leaderboard period, in collateral token
   */
  v: Amount;
  /**
   * PnL×√volume composite ranking score (PnL times the integer square root of
   * volume). A pure ordering key in mixed units, not a currency amount; the
   * ranking key when sorting by "psv".
   */
  s: Amount;
  /**
   * Profile badges, encoded as "collection/badge" strings
   */
  b?: string[];
}
/**
 * Builder leaderboard: what each builder code contributed over the period
 */
export interface BuilderLeaderboard {
  /**
   * Block/timestamp of the latest update
   */
  at: BlockTimestamp;
  /**
   * Totals across every builder over the period
   */
  tot: BuilderLeaderboardTotals;
  /**
   * Builder data, sorted according to requested order
   */
  d: BuilderLeaderboardEntry[];
}
/**
 * Builder leaderboard totals across all builders over the period
 */
export interface BuilderLeaderboardTotals {
  /**
   * Total volume traded through builders over the period, in collateral token
   */
  v: Amount;
  /**
   * Total fees generated for the exchange by builder-attributed trading over
   * the period, in collateral token
   */
  pf: Amount;
  /**
   * Total fees earned by builders over the period, in collateral token
   */
  bf: Amount;
  /**
   * Total number of builder-attributed trades over the period
   */
  t: number /* uint64 */;
  /**
   * Number of distinct accounts that traded through any builder over the
   * period. Counted once per account, so this is lower than the sum of the
   * per-builder counts when an account trades through several builders.
   */
  u: number /* uint64 */;
}
/**
 * Builder leaderboard entry
 */
export interface BuilderLeaderboardEntry {
  /**
   * Rank (1-based), for the requested sorting
   */
  i: number /* uint32 */;
  /**
   * Builder code
   */
  id: number /* uint32 */;
  /**
   * Builder display name, empty for a code that is not registered
   */
  n?: string;
  /**
   * Volume traded through this builder over the period, in collateral token
   */
  v: Amount;
  /**
   * Fees this builder's trading generated for the exchange over the period, in
   * collateral token
   */
  pf: Amount;
  /**
   * Fees this builder earned over the period, in collateral token, before the
   * configured payout share is applied — see the builder payouts endpoint for
   * amounts actually paid
   */
  bf: Amount;
  /**
   * Number of trades attributed to this builder over the period. A trade where
   * the builder is on both sides counts twice.
   */
  t: number /* uint64 */;
  /**
   * Number of distinct accounts that traded through this builder over the
   * period
   */
  u: number /* uint64 */;
  /**
   * Number of markets this builder traded over the period
   */
  m: number /* uint32 */;
  /**
   * Share of the total builder volume over the period, in bps
   */
  s: number /* uint32 */;
}
/**
 * TradingMakerStatsMaxDays bounds the window `TradingMakerStats` will serve, in
 * whole UTC days. A response carries one bucket per day per market per account,
 * so an unbounded window would grow without limit as history accumulates.
 */
export const TradingMakerStatsMaxDays = 366;
/**
 * Market making statistics for one wallet over a window of whole UTC days.
 * Figures are summed over every exchange account the wallet holds and over every
 * day in the window, so a market is reported once whichever account traded it.
 * Quoting is measured per account before that summation: a wallet resting its bid
 * on one account and its ask on another reads as one-sided on both, not two-sided.
 */
export interface MakerStats {
  /**
   * Block/timestamp of the most recent statistics carried by the response
   */
  at: BlockTimestamp;
  /**
   * Start of the first UTC day covered (ms). Not the requested `from`: the
   * window expands to whole days, so this is that day's 00:00:00 UTC.
   */
  from: number /* uint64 */;
  /**
   * Start of the last UTC day covered (ms), inclusive — the day runs to its
   * own end, so the window covers everything up to `to` + 24h.
   */
  to: number /* uint64 */;
  /**
   * Per-market statistics, keyed by market ID. Markets the wallet neither
   * traded nor quoted in the window are absent rather than reported as
   * zeroes.
   */
  d: { [key: MarketID]: MakerMarketStats };
}
/**
 * Market making statistics for one market.
 * Every figure is reported for the maker (resting) side, the taker (aggressing)
 * side, and the two summed, so a wallet that both quotes and aggresses in the
 * same market can be read either way round without the client differencing.
 */
export interface MakerMarketStats {
  /**
   * Volume filled as the maker (resting side) over the window, CNS
   */
  mv: Amount;
  /**
   * Volume filled as the taker (aggressing side) over the window, CNS
   */
  tv: Amount;
  /**
   * Total volume filled over the window, CNS — the two above summed
   */
  v: Amount;
  /**
   * Fees paid on maker fills over the window, CNS. Gross: what the fill cost,
   * including any builder fee charged on top of the exchange fee. Never
   * negative — a maker rebate is not charged as a fee.
   */
  mfee: Amount;
  /**
   * Fees paid on taker fills over the window, CNS, gross as above
   */
  tfee: Amount;
  /**
   * Total fees paid over the window, CNS — the two above summed
   */
  fee: Amount;
  /**
   * Number of fills where the wallet was the maker
   */
  mf: number /* uint64 */;
  /**
   * Number of fills where the wallet was the taker
   */
  tf: number /* uint64 */;
  /**
   * Number of fills over the window, counting both sides. A fill where the
   * wallet is both maker and taker counts twice.
   */
  f: number /* uint64 */;
  /**
   * Average maker volume per maker fill over the window, CNS, rounded down.
   * Zero when nothing filled on that side.
   */
  mas: Amount;
  /**
   * Average taker volume per taker fill over the window, CNS, rounded down
   */
  tas: Amount;
  /**
   * Average volume per fill over the window across both sides, CNS, rounded
   * down. Zero when nothing filled.
   */
  as: Amount;
  /**
   * Quote quality figures that do not depend on the band. Absent when the
   * market was never observed over the window — which is not the same as a
   * market that was observed and never quoted, and is why this is omitted
   * rather than reported as zeroes.
   */
  q?: MakerQuoteStats;
  /**
   * One entry per configured band, tightest first. Absent when the market
   * was never observed over the window.
   */
  qb?: MakerQuoteBandStats[];
}
/**
 * Quote quality for one market over the window, across the bands together.
 * Every figure is an average over the window as a whole, formed from totals
 * summed across the days it covers: a busy day therefore weighs more than a
 * quiet one, and averaging these across windows does not reproduce them.
 * Every fraction and price distance below is in 10^-6 fractions: 1 000 000 is
 * the whole, and 10 000 is one percent. The distances are measured in millionths
 * of the mark price to begin with, so this is the unit they were accumulated in
 * rather than a rounding of a fraction.
 */
export interface MakerQuoteStats {
  /**
   * Fraction of the window during which the market could be quoted at all:
   * unpaused, and priced. 1 000 000 means it was scoreable throughout.
   */
  cov: Micros;
  /**
   * Average distance between the wallet's own best bid and best ask while it
   * was quoting both sides, as a fraction of the mark price. Zero when it
   * never quoted both sides inside the widest band.
   */
  sp: Micros;
  /**
   * Average distance from the mark price to the wallet's own best bid, and to
   * its own best ask, as a fraction of that price. Either can be negative: a
   * side quoted through the mark still counts, and reads as the distance past
   * it. The two sum to the average spread, and the difference between them is
   * the inventory skew — a positive one meaning the bid sits further from the
   * mark than the ask, so the wallet is leaning to sell.
   */
  bd: Micros;
  ad: Micros;
}
/**
 * Quote quality for one market inside one band. A band is a distance from the
 * mark price and a minimum notional; an order counts towards the band while it
 * rests inside the former, and a side counts as quoted while at least the latter
 * rests there.
 */
export interface MakerQuoteBandStats {
  /**
   * Maximum distance from the mark price at which an order counts, in basis
   * points, and the minimum notional that must rest inside it, CNS. The band
   * is reported in the unit it was configured in; the distances it bounds are
   * in 10^-6 fractions, which is that figure times 100.
   */
  bps: number /* uint32 */;
  mn: Amount;
  /**
   * Fraction of the window with the required notional resting on both sides,
   * on the bid, and on the ask, where 1 000 000 is the whole of it. Each side
   * is at least the two-sided figure, so the difference is time spent quoting
   * one side only.
   */
  u: Micros;
  ub: Micros;
  ua: Micros;
  /**
   * Average size and notional resting inside the band, LNS and CNS, rounded
   * down. Averaged over the whole window rather than only while quoting, so
   * time spent away from the band counts as zero.
   */
  bs: Size;
  as: Size;
  bn: Amount;
  an: Amount;
  /**
   * The wallet's share of everything resting inside the band, its own orders
   * included. 1 000 000 means it was the only liquidity in the band. The share
   * falls when another participant arrives, so it moves for reasons outside
   * the wallet's own quoting.
   */
  bls: Micros;
  als: Micros;
}

//////////
// source: apikey.go

/**
 * API key scope bitmask. trade implies read; withdrawals are never permitted via
 * an API key regardless of scope.
 */
export type ScopeMask = number /* uint32 */;
/**
 * Scope flags.
 * Append-only, values can be renamed but never should be removed.
 */
export const ScopeRead: ScopeMask = 1 << 0; // read account/order/position data
/**
 * Scope flags.
 * Append-only, values can be renamed but never should be removed.
 */
export const ScopeTrade: ScopeMask = 1 << 1; // place/cancel/modify orders (implies read)
/**
 * ScopeAll is the full scope carried by interactive wallet (JWT) sessions.
 */
export const ScopeAll = ScopeRead | ScopeTrade;
/**
 * ApiKeyPayloadRequest requests the EIP-712 message to sign for enrolling an API key.
 * Mirrors AuthPayloadRequest but carries the key parameters being authorized.
 */
export interface ApiKeyPayloadRequest {
  chain_id: number /* uint64 */;
  address: string; // signer wallet (owner/operator)
  public_key: string; // Ed25519 public key (32 bytes)
  scope_mask: ScopeMask; // bitmask: 1=read, 2=trade
  label: string; // human-readable key label (mandatory)
  expires_at?: number /* uint64 */; // Timestamp (ms), 0 = none
  ip_cidrs?: string[];
  target_profile?: string; // Target delegated account, if applicable
  /**
   * Builder code to bind this key to. Omit or 0 for an ordinary (non-builder) key.
   */
  builder_id?: number /* uint32 */;
  /**
   * Maximum builder fee this key may request per order, in hundred-thousandths
   * (1 = 0.1 bps, 1000 = 1%). This is an upper bound, not a fee: each order states
   * its own fee, up to this value.
   * Must be 0 for a key without the trade scope: such a key cannot place orders, so
   * it carries the builder code for attribution only and can never charge a fee.
   */
  max_builder_fee_per_100k?: number /* uint32 */;
}
export interface ApiKeyPayloadResponse {
  typed_data?: any /* apitypes.TypedData */;
  mac: string;
}
/**
 * ApiKeyEnrollRequest submits the signed enrollment payload. PopSignature is the
 * mandatory Ed25519 proof-of-possession over the typed-data hash.
 */
export interface ApiKeyEnrollRequest {
  chain_id: number /* uint64 */;
  address: string;
  typed_data?: any /* apitypes.TypedData */;
  mac: string;
  signature: string;
  pop_signature: string;
  target_profile?: string; // Target delegated account, if applicable
}
/**
 * ApiKeyInfo is the public, non-secret view of an enrolled key (there is no
 * secret to return).
 */
export interface ApiKeyInfo {
  /**
   * ApiKey is the opaque identifier the client sends in the X-API-Key header
   * on each request. Issued at enrollment.
   */
  api_key: string;
  address: string;
  scope_mask: ScopeMask;
  label: string;
  ip_cidrs: string[];
  origin: string; // HTTP Origin the key was enrolled from
  /**
   * Builder code the key is bound to. Zero for an ordinary (non-builder) key, in
   * which case the remaining builder fields are zero/empty too.
   */
  builder_id?: number /* uint32 */;
  /**
   * Builder display name. May be empty if the builder is no longer registered;
   * display `builder_id` in that case.
   */
  builder_name?: string;
  /**
   * Upper bound on the per-order builder fee, in hundred-thousandths
   * (1 = 0.1 bps, 1000 = 1%).
   */
  max_builder_fee_per_100k?: number /* uint32 */;
  /**
   * `max_builder_fee_per_100k` pre-formatted as a percentage, for display.
   */
  max_builder_fee_pct?: string; // e.g. "0.500%"
  expires_at: number /* uint64 */; // Timestamp (ms), 0 = none
  last_used_at: number /* uint64 */; // Timestamp (ms), 0 = never
  created_at: number /* uint64 */; // Timestamp (ms)
}
export interface ApiKeyEnrollResponse {
  api_key: ApiKeyInfo;
}
export interface ApiKeyListResponse {
  api_keys: ApiKeyInfo[];
}
export interface ApiKeyDeleteRequest {
  /**
   * ApiKey is the X-API-Key identifier of the key to revoke.
   */
  api_key: string;
}

//////////
// source: auth.go

export interface AuthPayloadRequest {
  chain_id: number /* uint64 */;
  address: string;
}
export interface AuthPayloadResponse {
  message?: string;
  nonce?: string;
  issued_at?: number /* uint64 */; // Timestamp (ms)
  typed_data?: any /* apitypes.TypedData */;
  mac: string;
}
export interface AuthConnectRequest {
  chain_id: number /* uint64 */;
  address: string;
  message?: string;
  nonce?: string;
  issued_at?: number /* uint64 */; // Timestamp (ms)
  typed_data?: any /* apitypes.TypedData */;
  mac: string;
  signature: string;
  ref_code?: string;
  target_profile?: string;
}
export interface AuthConnectResponse {
  nonce: string;
  expires_at: number /* uint64 */; // Timestamp (ms)
  available_profiles: string[];
  referrer?: string;
}
export interface AuthSignInRequest extends MessageHeader {
  chain_id: number /* uint64 */;
  /**
   * Accompanies JWT delivered in cookie
   */
  nonce: string;
}
/**
 * ApiKeySignInRequest is the first WebSocket frame for API-key authentication.
 * The signature covers the WS sign-in canonical string (chain_id, "trading-ws-signin",
 * timestamp, nonce).
 */
export interface ApiKeySignInRequest extends MessageHeader {
  chain_id: number /* uint64 */;
  api_key: string; // X-API-Key identifier issued at enrollment
  timestamp: string; // unix epoch milliseconds, decimal
  nonce: string; // client-random, base64url
  signature: string; // base64url(ed25519 signature)
}
export interface AuthWaitlistRequest extends AuthConnectRequest {
  contact: string;
  x_challenge: string;
}

//////////
// source: common.go

export type ChainID = number /* uint64 */;
export type InstanceID = number /* uint32 */;
export type TokenID = number /* uint32 */;
export type MarketID = number /* uint32 */;
export type PerpetualID = number /* uint32 */;
export type FeeLevelID = number /* uint32 */;
export type AccountID = number /* uint64 */;
export type OrderID = number /* uint64 */;
export type RequestID = number /* uint64 */;
export type PositionID = number /* uint64 */;
export type Decimals = number /* uint8 */;
export type Fraction = number /* uint32 */;
export type Micros = number /* int64 */;
export type Amount = string;
export type Price = number /* uint64 */;
export type SPrice = number /* int64 */;
export type Size = number /* uint64 */;
export type StatusCode = number /* int32 */;
export const StatusCodeSuccess: StatusCode = 0;
export const StatusCodeBadRequest: StatusCode = 400;
export const StatusCodeForbidden: StatusCode = 403;
export const StatusCodeNotFound: StatusCode = 404;
export const StatusCodeTooManyRequests: StatusCode = 429;
export const StatusInternalServerError: StatusCode = 500;
/**
 * Temporarily unable to serve the request; retrying later is expected to
 * succeed. Answered while the gateway is still catching up with the chain.
 */
export const StatusCodeServiceUnavailable: StatusCode = 503;
/**
 * Versioned data header
 */
export interface VersionHeader {
  ver: number /* int64 */;
}
/**
 * Streaming message header,
 * mandatory for each WebSocket message in both directions
 */
export interface MessageHeader {
  /**
   * Type of the message defining its semantics
   */
  mt: MessageType;
  /**
   * ID of the subscription produced this message, if applicable,
   * unique within a single WS connection
   */
  sid?: number /* uint32 */;
  /**
   * Sequence number of the message within a specific stream.
   * General rule is that messages with `SeqNo` seen before should be
   * ignored, while whether gaps allowed or not depends on the stream
   * semantics (see `SubscriptionOp`).
   */
  sn?: number /* uint64 */;
  /**
   * Message correlation ID, eg. SeqNo of the request produced this response,
   * if applicable
   */
  cid?: number /* uint64 */;
  /**
   * ID of the user session this request sent within
   */
  ses?: string;
}
/**
 * Block number and timestamp
 */
export interface BlockTimestamp {
  /**
   * Block number
   */
  b?: number /* uint64 */;
  /**
   * Timestamp (ms)
   */
  t?: number /* uint64 */;
}
/**
 * Block number, timestamp and transaction index within the block
 */
export interface BlockTxTimestamp {
  /**
   * Block number
   */
  b?: number /* uint64 */;
  /**
   * Timestamp (ms)
   */
  t?: number /* uint64 */;
  /**
   * Index of the trade transaction within a block
   */
  tx?: number /* uint32 */;
  /**
   * Transaction hash
   */
  txid?: string;
}
/**
 * Block number, timestamp, transaction and log indexes within the block
 */
export interface BlockTxLogTimestamp {
  /**
   * Block number
   */
  b?: number /* uint64 */;
  /**
   * Timestamp (ms)
   */
  t?: number /* uint64 */;
  /**
   * Index of the transaction within a block
   */
  tx?: number /* uint32 */;
  /**
   * Transaction hash
   */
  txid?: string;
  /**
   * Index of the log within a transaction
   */
  l?: number /* uint32 */;
}
/**
 * Status of the operation
 */
export interface Status {
  /**
   * Error code, operation/endpoint specific. 0 means success
   */
  code: StatusCode;
  /**
   * Human-readable error description
   */
  error?: string;
}
/**
 * Generic status response
 */
export interface StatusResponse extends MessageHeader {
  status: Status;
}
/**
 * Status response to a request carrying several operations
 * MsgType = MsgTypeBatchStatusResponse
 * One status per operation of the request, in the order the request listed them,
 * so an operation is identified by its position: the i-th status answers the
 * i-th operation. Operations are judged one by one and do not share a fate - a
 * batch is not a transaction, and some of it succeeding while the rest is
 * refused is the normal outcome, not an error.
 */
export interface BatchStatusResponse extends MessageHeader {
  /**
   * Status of the request as a whole. Zero when the operations were judged
   * individually, in which case `statuses` carries their outcomes. A non-zero
   * code means the request was refused before any operation was looked at:
   * nothing was acted on and `statuses` is empty.
   */
  status: Status;
  /**
   * Status of each operation of the request, in the order the request listed
   * them. Empty when `status` refused the request as a whole.
   */
  statuses?: Status[];
}
/**
 * Ping request from the client to server.
 * MsgType = MsgTypePing
 * Supported by trading API only, for market-data API it is expected
 * to receive at least one message per block anyway
 */
export interface Ping extends MessageHeader {
  /**
   * Client-side timestamp (ms) of the request
   */
  t?: number /* uint64 */;
}
/**
 * Pong response for the Ping
 * MsgType = MsgTypePong
 */
export interface Pong extends MessageHeader {
  /**
   * Client-side timestamp (ms) of the request from the original
   * Ping message
   */
  t?: number /* uint64 */;
}

//////////
// source: context.go

/**
 * Protocol operational context
 */
export interface Context {
  /**
   * Chain the protocol is operating on
   */
  chain: Chain;
  /**
   * Protocol instances available on the chain
   */
  instances: ProtocolInstance[];
  /**
   * Tokens supported as a collateral
   */
  tokens: Token[];
  /**
   * Markets available for trading
   */
  markets: Market[];
  /**
   * Application feature flags
   */
  features?: { [key: string]: string };
  /**
   * Geo blocking restrictions
   */
  geo_block?: string[];
  /**
   * Rewards configuration
   */
  rewards: RewardsConfig;
  /**
   * Trading competitions
   */
  competitions?: Competition[];
  /**
   * Incentive NFT/SBT collections whose ownership grants benefits. Clients
   * can check holdings directly on-chain using these addresses.
   */
  incentive_tokens?: IncentiveToken[];
}
/**
 * IncentiveToken is a configured incentive NFT/SBT collection.
 */
export interface IncentiveToken {
  /**
   * Token contract address.
   */
  address?: string;
  /**
   * Token standard governing the ownership check ("nft"|"sbt").
   */
  kind: string;
}
/**
 * Chain the protocol is operating on
 */
export interface Chain extends VersionHeader {
  /**
   * ChainID as per https://eips.ethereum.org/EIPS/eip-155
   */
  chain_id: ChainID;
  /**
   * Name of the chain
   */
  name?: string;
  /**
   * Сhain icon IDs or URLs
   */
  icons?: string[];
  /**
   * Native token of the chain
   */
  native_token?: Token;
  /**
   * URLs of available public RPC nodes
   */
  rpc_urls?: string[];
  /**
   * URLs of available block explorers
   */
  block_explorer_urls?: string[];
  /**
   * Gas price statistics.
   * When sending a transaction, choose the level to use as `maxFeePerGas`
   * depending on the desired transaction priority
   */
  gas: GasPrice;
}
/**
 * Native or ERC-20 token
 */
export interface Token extends VersionHeader {
  /**
   * Internal ID of the token
   */
  id?: TokenID;
  /**
   * Address of the ERC-20 token smart contract,
   * empty for native token
   */
  address?: string;
  /**
   * Symbol of the token
   */
  symbol: string;
  /**
   * Name of the token
   */
  name: string;
  /**
   * Token icon ID or URL
   */
  icon?: string;
  /**
   * Number of digits after the decimal point encoded
   * in integer amount representation
   */
  decimals: Decimals;
  /**
   * Display format for amounts
   */
  display_precision: Decimals;
  /**
   * Name of the index to use for USD conversion ratio
   */
  usd_index?: string;
}
/**
 * Gas prices to be used as `maxFeePerGas` for user transactions,
 * according to the desired transaction priority
 */
export interface GasPrice {
  /**
   * Block number/timestamp these prices are produced at
   */
  at: BlockTimestamp;
  /**
   * Number of the head block (latest known)
   */
  h: number /* uint64 */;
  /**
   * Maximum priority
   */
  max: Amount;
  /**
   * Within top ~5%
   */
  p95: Amount;
  /**
   * Within top ~50%
   */
  p50: Amount;
  /**
   * Minimum priority
   */
  min: Amount;
  /**
   * Base fee only
   */
  base: Amount;
}
/**
 * Gas price update notification
 */
export interface GasPriceUpdate extends MessageHeader {
  /**
   * Data
   */
  d: GasPrice;
}
/**
 * Chain heartbeat notification,
 * indicates the latest block processed by the indexer
 */
export interface Heartbeat extends MessageHeader {
  /**
   * Number of latest known head block
   */
  h: number /* uint64 */;
}
/**
 * Trading competition with a fixed time window
 */
export interface Competition extends VersionHeader {
  /**
   * URL-friendly unique identifier
   */
  slug: string;
  /**
   * Human-readable competition name
   */
  name: string;
  /**
   * Competition window start, ms (inclusive)
   */
  starts_at: number /* uint64 */;
  /**
   * Competition window end, ms (exclusive)
   */
  ends_at: number /* uint64 */;
  /**
   * Minimum trading volume to qualify for the volume leaderboard, CNS
   */
  vol_min_volume: Amount;
  /**
   * Minimum trading volume to qualify for the ROI leaderboard, CNS
   */
  roi_min_volume: Amount;
  /**
   * Minimum trading volume to qualify for the PnL leaderboard, CNS
   */
  pnl_min_volume: Amount;
  /**
   * Minimum trading volume to qualify for the PnL×√volume leaderboard, CNS
   */
  psv_min_volume: Amount;
  /**
   * Total prize pool distributed among winners, CNS
   */
  pool_amount: Amount;
  /**
   * Per-rank prize pool distribution for the volume leaderboard, bps of
   * PoolAmount (index 0 = 1st place, index 1 = 2nd place, ...)
   */
  vol_pool_distribution_bps?: number /* uint32 */[];
  /**
   * Per-rank prize pool distribution for the ROI leaderboard, bps of
   * PoolAmount (index 0 = 1st place, index 1 = 2nd place, ...)
   */
  roi_pool_distribution_bps?: number /* uint32 */[];
  /**
   * Per-rank prize pool distribution for the PnL leaderboard, bps of
   * PoolAmount (index 0 = 1st place, index 1 = 2nd place, ...)
   */
  pnl_pool_distribution_bps?: number /* uint32 */[];
  /**
   * Per-rank prize pool distribution for the PnL×√volume leaderboard, bps of
   * PoolAmount (index 0 = 1st place, index 1 = 2nd place, ...)
   */
  psv_pool_distribution_bps?: number /* uint32 */[];
  /**
   * Whether this competition's results are final. False while the competition
   * is running or its rankings/prizes are still being settled; true once
   * results are official.
   */
  results_finalized: boolean;
}
/**
 * Rewards configuration
 */
export interface RewardsConfig extends VersionHeader {
  /**
   * Referral's trading volume threshold to become active, CNS
   */
  active_referral_min_volume: Amount;
  /**
   * Referral's max age in days to remain active
   */
  active_referral_max_age_days: number /* uint32 */;
  /**
   * Trading volume threshold to unlock new invites, CNS
   */
  invite_volume_threshold: Amount;
  /**
   * Number of referral invites given per trading volume threshold
   */
  invites_per_threshold: number /* uint32 */;
  /**
   * Minimal period from ref code creation to allow renewal, seconds
   */
  ref_code_renew_ttl_sec: number /* uint64 */;
  /**
   * Referral rewards share for first-level referals,bps
   */
  referrer_1st_reward_share_bps: number /* uint32 */;
  /**
   * Referral rewards share for second-level referals,bps
   */
  referrer_2nd_reward_share_bps: number /* uint32 */;
  /**
   * Minimum trading volume to be eligible to apply for the partner program,
   * CNS.
   */
  partner_min_volume: Amount;
  /**
   * Rewards epoch duration in seconds
   */
  rewards_epoch_duration_sec: number /* uint64 */;
  /**
   * First rewards epoch starts at, ms
   */
  rewards_epoch_starts_at: number /* uint64 */;
  /**
   * Points epoch duration in seconds
   */
  points_epoch_duration_sec?: number /* uint64 */;
  /**
   * First Points epoch starts at, ms
   */
  points_epoch_starts_at?: number /* uint64 */;
  /**
   * Total number of points distributed per epoch
   */
  points_per_epoch?: Amount;
  /**
   * Share of a referral's points the referrer additionally earns, bps
   */
  referrer_points_share_bps?: number /* uint32 */;
  /**
   * Share of its own points the referred account additionally earns as a
   * bonus, bps
   */
  referral_points_share_bps?: number /* uint32 */;
  /**
   * Builder payout epoch duration in seconds. Absent while the builder payout
   * program is not running.
   */
  builder_epoch_duration_sec?: number /* uint64 */;
  /**
   * First builder payout epoch starts at, ms. Epoch N (1-based) runs from
   * `builder_epoch_starts_at + (N - 1) * builder_epoch_duration_sec`.
   */
  builder_epoch_starts_at?: number /* uint64 */;
  /**
   * Share of the builder fees a builder code accrues that is paid out to it,
   * bps. 10000 = the whole fee.
   */
  builder_payout_share_bps?: number /* uint32 */;
  /**
   * MPoints epoch duration in seconds
   */
  mpoints_epoch_duration_sec?: number /* uint64 */;
  /**
   * First MPoints epoch starts at, ms
   */
  mpoints_epoch_starts_at?: number /* uint64 */;
  /**
   * Number of MPoints epochs
   */
  mpoints_num_epochs?: number /* uint32 */;
  /**
   * Total MPoints pool size
   */
  mpoints_pool_size?: number /* uint64 */;
  /**
   * Total MPoints pool amount, CNS
   */
  mpoints_pool_amount?: Amount;
}

//////////
// source: endpoints.go

export const AuthNonceHeader = 'X-Auth-Nonce';
export const BrowserSessionHeader = 'X-Browser-Session-ID';
/**
 * UserCountryHeader is a RESPONSE header: the country your request was
 * resolved to, returned on the public context endpoint. Sending it on a
 * request has no effect.
 */
export const UserCountryHeader = 'X-User-Country';
/**
 * API key authentication headers (per-request signature scheme).
 * Presence of APIKeyHeader selects API-key auth for the request.
 */
export const APIKeyHeader = 'X-API-Key';
/**
 * API key authentication headers (per-request signature scheme).
 * Presence of APIKeyHeader selects API-key auth for the request.
 */
export const APIKeyTimestampHeader = 'X-API-Timestamp';
/**
 * API key authentication headers (per-request signature scheme).
 * Presence of APIKeyHeader selects API-key auth for the request.
 */
export const APIKeyNonceHeader = 'X-API-Nonce';
/**
 * API key authentication headers (per-request signature scheme).
 * Presence of APIKeyHeader selects API-key auth for the request.
 */
export const APIKeySignatureHeader = 'X-API-Signature'; // base64url(ed25519 signature)
export const WebSocketCloseUnauthorized = 3401;
export const Ver1 = '/v1';
/**
 * [GET] Returns global product configuration.
 * [Authentication: no / JWT cookie + `AuthNonceHeader` / API key]
 */
export const PubContextPath = Ver1 + '/pub/context';
/**
 * [POST] Returns payload for wallet authentication flow.
 * Status codes with special meaning:
 * 451 (Unavailable For Legal Reasons) - client's country is blocked
 * [Authentication: no]
 */
export const AuthPayloadPath = Ver1 + '/auth/payload';
/**
 * [POST] Accepts signed payload and referral code to authenticate the wallet
 * and create the corresponding profile/link referrals for unknown wallets.
 * Status codes with special meaning:
 * 418 (Teapot) - access code required
 * 423 (Locked) - access code is invalid/exhausted
 * 403 (Forbidden) - access for account is denied
 * 451 (Unavailable For Legal Reasons) - client's country is blocked
 * [Authentication: no]
 */
export const AuthConnectPath = Ver1 + '/auth/connect';
/**
 * [POST] Accepts signed payload and waitlist contact info to validate the wallet
 * and add waitlist entry.
 * Status codes with special meaning:
 * 451 (Unavailable For Legal Reasons) - client's country is blocked
 * [Authentication: no]
 */
export const AuthWaitlistPath = Ver1 + '/auth/waitlist';
/**
 * [POST] Returns the EIP-712 payload to sign for enrolling an API key.
 * Validates key parameters (scope, expiry, IP CIDRs, public key uniqueness)
 * and profile existence (incl. delegated accounts).
 * [Authentication: no — wallet signature gates enrollment]
 */
export const ApiKeyPayloadPath = Ver1 + '/api-key/payload';
/**
 * [POST] Accepts the signed payload + Ed25519 proof-of-possession and enrolls
 * the API key for the resolved profile.
 * Status codes with special meaning:
 * 404 (Not Found) - target profile not found
 * 409 (Conflict) - public key already registered
 * 423 (Locked) - per-profile key limit reached
 * [Authentication: no — wallet signature gates enrollment]
 */
export const ApiKeyEnrollPath = Ver1 + '/api-key/enroll';
/**
 * [GET] Lists the authenticated profile's non-revoked API keys.
 * [Authentication: JWT cookie + `AuthNonceHeader` only (no API key)]
 */
export const ApiKeyListPath = Ver1 + '/api-key/list';
/**
 * [POST] Soft-revokes one of the authenticated profile's API keys.
 * [Authentication: JWT cookie + `AuthNonceHeader` only (no API key)]
 */
export const ApiKeyDeletePath = Ver1 + '/api-key/delete';
/**
 * [POST] X OAuth2 token and user info proxy to overcome CORS issues.
 * Receives the request body to pass to `https://api.x.com/2/oauth2/token` endpoint,
 * retrieves the access token and queries `https://api.x.com/2/users/me` for the username.
 * [Authentication: no]
 */
export const OAuthXUserName = Ver1 + '/oauth/x-user-name';
/**
 * [GET] Returns current referral code for the profile.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfileRefCode = Ver1 + '/profile/ref-code';
/**
 * [POST] Regenerates a new referral code for the profile.
 * Regeneration is available only if current referral code was created
 * more than `ref_code_min_ttl` configuration parameter ago.
 * [Authentication: JWT cookie + `AuthNonceHeader` only (no API key)]
 */
export const ProfileRefCodeRenew = Ver1 + '/profile/ref-code/renew';
/**
 * [POST] Links the authenticated profile to a referrer identified by the provided ref code.
 * Allowed only when the profile is not already referred by someone else
 * (self-referral at registration is treated as not-yet-referred).
 * Status codes with special meaning:
 * 409 (Conflict) - profile is already referred, or self-referral attempted
 * 423 (Locked) - ref code is invalid/exhausted
 * [Authentication: JWT cookie + `AuthNonceHeader` only (no API key)]
 */
export const ProfileReferredBy = Ver1 + '/profile/referred-by';
/**
 * [GET] Returns list of referrals for the profile.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfileReferrals = Ver1 + '/profile/referrals';
/**
 * [GET] Returns list of reward payouts for the profile.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfileRewards = Ver1 + '/profile/rewards';
/**
 * [POST] Updates referral memo.
 * [Authentication: JWT cookie + `AuthNonceHeader` only (no API key)]
 */
export const ProfileReferralMemo = Ver1 + '/profile/referrals/:address/memo';
/**
 * [GET] Returns list of Points rewards for the profile.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfilePoints = Ver1 + '/profile/points';
/**
 * [GET] Returns list of MPoints rewards for the profile.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfileMPoints = Ver1 + '/profile/mpoints';
/**
 * [GET] Returns the currently active boosts (highest per type) for the profile.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfileBoosts = Ver1 + '/profile/boosts';
/**
 * [GET] Returns the incentive programs the profile is enrolled in (empty when none).
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfilePrograms = Ver1 + '/profile/programs';
/**
 * [GET] Returns all-time headline figures for every builder code registered to
 * the calling wallet, one row per code, ranked on the public builder
 * leaderboard's volume ranking.
 * A wallet that holds no builder codes is served an empty list, not an error.
 * Note that an API key a builder enrolled on a USER's behalf authenticates as
 * that user, not as the builder: a builder reaches this endpoint as the wallet
 * its codes are registered to.
 * Status codes with special meaning:
 * 503 (Service Unavailable) - the builder registry is momentarily unavailable;
 * retry shortly. Distinguished from an empty list, which means the wallet holds
 * no codes.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfileBuilderStats = Ver1 + '/profile/builder/stats';
/**
 * [GET] Returns the per-epoch payout history for every builder code the calling
 * wallet holds, newest epoch first.
 * Every closed epoch is reported for every code, including the epochs a code
 * earned nothing in — those carry zeroes rather than being omitted.
 * The epoch in flight has no row: its window comes from
 * `builder_epoch_starts_at` / `builder_epoch_duration_sec` on the rewards
 * configuration.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfileBuilderPayouts = Ver1 + '/profile/builder/payouts';
/**
 * [GET] Returns contact info for the profile.
 * [POST] Stores contact info for the profile.
 * [Authentication: GET — JWT cookie + `AuthNonceHeader` / API key;
 * POST — JWT cookie + `AuthNonceHeader` only (no API key)]
 */
export const ProfileContactInfo = Ver1 + '/profile/contact-info';
/**
 * [GET] Returns the display-name alias for the profile ("" when unset).
 * [POST] Stores the display-name alias for the profile.
 * [Authentication: GET — JWT cookie + `AuthNonceHeader` / API key;
 * POST — JWT cookie + `AuthNonceHeader` only (no API key)]
 */
export const ProfileAlias = Ver1 + '/profile/alias';
/**
 * [GET] Returns settings for the profile (arbitrary client-defined JSON object,
 * private to the profile), "{}" if none stored yet.
 * [POST] Stores settings for the profile, replacing the previous value.
 * Status codes with special meaning:
 * 400 (Bad Request) - settings are not a valid JSON object
 * 413 (Payload Too Large) - settings exceed `ProfileSettingsMaxSize`
 * [Authentication: GET — JWT cookie + `AuthNonceHeader` / API key;
 * POST — JWT cookie + `AuthNonceHeader` only (no API key)]
 */
export const ProfileSettings = Ver1 + '/profile/settings';
/**
 * [GET] Returns current announcements for the profile (if authenticated) or public audience.
 * [Authentication: no / JWT cookie + `AuthNonceHeader` / API key]
 */
export const ProfileAnnouncements = Ver1 + '/profile/announcements';
/**
 * [GET] Returns candlestick data for the requested market, resolution and period of time.
 * A market with no candles yet returns an empty series.
 * [Authentication: no]
 */
export const MarketDataCandles = Ver1 + '/market-data/:market_id/candles/:resolution/:from-:to';
/**
 * [GET] Returns funding events of the requested market applied within the requested
 * period of time (`from` and `to` are millisecond timestamps, matched against the
 * block each event applies at). At least one event is returned when the market has
 * any, so a period without a funding event of its own still resolves to the rate
 * that was in force over it; a market with no funding events yet returns an empty
 * series.
 * Status codes with special meaning:
 * 400 (Bad Request) - the period covers more than `MaxFundingEventsPerRequest`
 * funding intervals of the market (`funding_interval_sec` of its configuration)
 * [Authentication: no]
 */
export const MarketDataFunding = Ver1 + '/market-data/:market_id/funding/:from-:to';
/**
 * [GET] Returns funding events of every market applied within the requested period
 * of time, keyed by market ID. The per-market series follow the rules of
 * `MarketDataFunding`; a market with no funding events yet is absent from the
 * response.
 * Status codes with special meaning:
 * 400 (Bad Request) - the period covers more than
 * `MaxAllMarketsFundingEventsPerRequest` funding intervals of the market with
 * the shortest funding interval
 * [Authentication: no]
 */
export const MarketDataAllFunding = Ver1 + '/market-data/funding/:from-:to';
/**
 * [GET] Returns the current L2 order book of the requested market: the price and
 * size of each resting level, each side ordered away from the spread. This is the
 * snapshot the `order-book` WebSocket stream opens with, served over HTTP for a
 * client that wants the book once rather than following it. The optional `levels`
 * query parameter bounds how many price levels each side carries, counted from the
 * spread outwards, and defaults to `MaxBookLevels`.
 * Status codes with special meaning:
 * 400 (Bad Request) - `levels` is not an integer between 1 and `MaxBookLevels`
 * 503 (Service Unavailable) - the book of the market is not available yet,
 * which is the case for a short while after the service starts
 * [Authentication: no]
 */
export const MarketDataBook = Ver1 + '/market-data/:market_id/book'; // ?levels=<level_count>
/**
 * [GET] Returns the current state of the requested market: its oracle, mark,
 * last and mid price, its best bid and ask, and its daily volume, open
 * interest and total value locked. Each market's state carries the block and
 * timestamp it is current as of. This is the state the `market-state`
 * WebSocket stream publishes, served over HTTP for a client that wants it
 * once rather than following it, and keyed by market ID the way the stream
 * keys it.
 * Status codes with special meaning:
 * 503 (Service Unavailable) - the state of the market is not available yet,
 * which is the case for a short while after the service starts
 * [Authentication: no]
 */
export const MarketDataTicker = Ver1 + '/market-data/:market_id/ticker';
/**
 * [GET] Returns the current state of every market, keyed by market ID. The
 * per-market state follows the rules of `MarketDataTicker`;
 * A Market whose state has not been received yet is abscent.
 * Status codes with special meaning:
 * 503 (Service Unavailable) - no market state is available yet, which is the
 * case for a short while after the service starts
 * [Authentication: no]
 */
export const MarketDataAllTicker = Ver1 + '/market-data/ticker';
/**
 * [GET] Returns historical account events.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingAccountHistory = Ver1 + '/trading/account-history'; // ?page=<cursor>&count=<item_count>
/**
 * [GET] Returns historical order fills.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingFillHistory = Ver1 + '/trading/fills'; // ?page=<cursor>&count=<item_count>
/**
 * [GET] Returns historical order events.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingOrderHistory = Ver1 + '/trading/order-history'; // ?page=<cursor>&count=<item_count>
/**
 * [GET] Returns historical position events.
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingPositionHistory = Ver1 + '/trading/position-history'; // ?page=<cursor>&count=<item_count>
/**
 * [GET] Returns portfolio data for the requested kind (equity|pnl) and period (all|day|week|month).
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingPortfolio = Ver1 + '/trading/portfolio/:kind/:period';
/**
 * [GET] Returns the calling wallet's market making statistics, per market, over a
 * window of whole UTC days. Restricted to wallets enrolled on the market maker
 * programme.
 * The window is given by the optional `from` and `to` query parameters, both
 * timestamps in milliseconds. Each end expands to cover the whole UTC day it falls
 * in, so a window is always one or more complete days; the days actually covered
 * are reported back on the response. `from` defaults to the start of `to`'s day and
 * `to` defaults to now, so with neither given the response covers the current UTC
 * day so far.
 * Status codes with special meaning:
 * 400 (Bad Request) - malformed window, or one longer than `TradingMakerStatsMaxDays`
 * 403 (Forbidden) - the wallet is not enrolled on the market maker programme
 * 404 (Not Found) - the wallet holds no exchange account
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingMakerStats = Ver1 + '/trading/maker-stats'; // ?from=<ts_ms>&to=<ts_ms>
/**
 * [GET] Returns leaderboard data for the aggregation period (all|day|week|month|2weeks) and sorting (pnl|roi|vol|psv).
 * "psv" ranks by PnL×√volume (the psv_score field on each entry).
 * [Authentication: no]
 */
export const TradingLeaderboard = Ver1 + '/trading/leaderboard/:period/:sorting';
/**
 * [GET] Returns the builder leaderboard for the aggregation period
 * (all|day|week|month|2weeks) and sorting (vol|fees|builder_fees|trades|traders).
 * "fees" ranks by the fees a builder generated for the exchange; "builder_fees" by
 * the fees the builder itself earned.
 * [Authentication: no]
 */
export const TradingBuilderLeaderboard = Ver1 + '/trading/builders/leaderboard/:period/:sorting';
/**
 * [GET] Returns the open orders of the calling wallet, across every account and
 * market it holds one in: the orders still resting on the book, together with
 * the trigger orders still waiting on their condition, as of the block the
 * trading state is current at. This is the snapshot the `orders` WebSocket
 * stream opens with, served over HTTP for a client that wants it once rather
 * than following it. A wallet with nothing open is served an empty list.
 * Status codes with special meaning:
 * 404 (Not Found) - the wallet holds no exchange account
 * [POST] Submits a batch of orders (`BatchOrderRequest`) for the authenticated
 * wallet: places, changes or cancels each of them, exactly as the equivalent
 * WebSocket frames do, and in the order they are listed. One order is a batch of
 * one; several in one request is the way to submit them at a single round trip of
 * latency.
 * Answers a `BatchStatusResponse` carrying one status per order, at the position
 * of the order it answers, each being the status the WebSocket connection would
 * return for that order. A zero code acknowledges the order as accepted for
 * forwarding to the exchange, not as placed or filled -- the outcome follows one
 * block later, on the WebSocket order updates or from a GET of this endpoint.
 * The batch is not a transaction: orders are judged one by one, and orders that
 * were accepted stay accepted when a later one is refused. Read every status,
 * not just the HTTP one, which is 200 whenever the orders were judged at all.
 * Each order must name an account the calling wallet owns, and an API key needs
 * the trade scope; the builder code, if any, is taken from that key and cannot
 * be named in an order.
 * A request refused as a whole -- unreadable, empty, longer than 100 orders, or
 * from a key without the trade scope -- carries no per-order statuses, reports
 * the reason as the batch status and repeats its code as the HTTP status.
 * Each order counts against the request rate limit as a request of its own
 * would, charged as the batch is processed: a batch that outruns the caller's
 * allowance is served up to that point, and each order past it is answered 429
 * in its own position and may be retried.
 * Status codes with special meaning:
 * 503 (Service Unavailable) - the gateway does not know the current block yet,
 * which is the case for a short while after the service starts
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingOrders = Ver1 + '/trading/orders';
/**
 * [GET] Returns the open positions of the calling wallet, across every account
 * and market it holds one in, as of the block the trading state is current at.
 * This is the snapshot the `positions` WebSocket stream opens with, served over
 * HTTP for a client that wants it once rather than following it. A wallet with
 * nothing open is served an empty list.
 * Status codes with special meaning:
 * 404 (Not Found) - the wallet holds no exchange account
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingPositions = Ver1 + '/trading/positions';
/**
 * [GET] Returns the calling wallet: its nonce and fee level, the exchange
 * accounts it owns and the all-time statistics of each, as of the block the
 * trading state is current at. This is the snapshot the `wallet` WebSocket
 * stream opens with, served over HTTP for a client that wants it once rather
 * than following it.
 * Status codes with special meaning:
 * 404 (Not Found) - the wallet holds no exchange account
 * [Authentication: JWT cookie + `AuthNonceHeader` / API key]
 */
export const TradingWallet = Ver1 + '/trading/wallet';

//////////
// source: market.go

export interface ProtocolInstance extends VersionHeader {
  /**
   * Internal ID of the protocol instance
   */
  id: InstanceID;
  /**
   * Address of the instance smart contract
   */
  address: string;
  /**
   * ID of the token used as a collateral
   */
  collateral_token_id: TokenID;
  /**
   * Mimimal deposit amount to open an account, integer scaled by `Decimals` of the collateral token
   */
  min_account_open_amount: Amount;
  /**
   * Mimimal deposit amount to top up an account, integer scaled by `Decimals` of the collateral token
   */
  min_deposit_amount: Amount;
  /**
   * Mimimal withdrawal amount, integer scaled by `Decimals` of the collateral token
   */
  min_withdraw_amount: Amount;
  /**
   * Maximal account equity limit, integer scaled by `Decimals` of the collateral token.
   * If set, deposits should be limited to <max_account_equity> - <total account value>
   */
  max_account_equity?: Amount;
  /**
   * Maximum total number of active trigger orders per account for the instance,
   * the market belongs to
   */
  max_account_trigger_orders: number /* uint32 */;
}
/**
 * Market available for trading
 */
export interface Market extends VersionHeader {
  /**
   * Internal ID of the market, unique across all protocol instances
   * and versions/generations of the perp with the same ID
   */
  id: MarketID;
  /**
   * ID of the protocol instance the market operates on
   */
  instance_id: InstanceID;
  /**
   * ID of the perpetual within the smart contract
   */
  perpetual_id: PerpetualID;
  /**
   * Symbol of the market
   */
  symbol: string;
  /**
   * Name of the market
   */
  name: string;
  /**
   * Units of size
   */
  size_units: string;
  /**
   * Market icon  ID or URL
   */
  icon: string;
  /**
   * Interval of the funding event in seconds
   */
  funding_interval_sec: number /* uint32 */;
  /**
   * Interval of the funding event in blocks
   */
  funding_interval_blocks: number /* uint64 */;
  /**
   * Number of blocks the order is allowed to execute within
   */
  order_ttl_blocks: number /* uint32 */;
  /**
   * Minimal number of blocks since the order posting when it is safe to
   * retry even if the status of original request is unknown
   */
  order_retry_blocks: number /* uint32 */;
  /**
   * Maximal market order slippage in basis points
   */
  order_max_market_slippage_bps: number /* uint32 */;
  /**
   * Maximum share of the position notional, in basis points, that may be used to
   * collateralize unrealized negative PnL when an order is filled. Applied to
   * orders that do not carry a value of their own
   */
  order_max_neg_pnl_collat_bps: number /* uint32 */;
  /**
   * Market configuration
   */
  config: MarketConfig;
  /**
   * Market state
   */
  state: MarketState;
  /**
   * Market latest funding event
   */
  funding: FundingEvent;
  /**
   * Description of the boost currently active on the market, to be
   * parsed by the frontend. Empty when there is no boost.
   */
  boost_params?: string;
}
/**
 * Market configuration
 */
export interface MarketConfig {
  /**
   * Block/timestamp the params valid from
   */
  at: BlockTimestamp;
  /**
   * Whether the market is open for trading
   */
  is_open: boolean;
  /**
   * Number of decimal digits in price integer representation
   */
  price_decimals: Decimals;
  /**
   * Number of decimal digits in size integer representation
   */
  size_decimals: Decimals;
  /**
   * Mimimal order amount to post, integer scaled by `Decimals` of the collateral token
   */
  min_posting_amount: Amount;
  /**
   * Mimimal order settlement amount, integer scaled by `Decimals` of the collateral token
   */
  min_settle_amount: Amount;
  /**
   * Funding sum scaling exponent, the funding sum should be divided by 10^<FundingSumScalingExp>
   */
  funding_sum_scaling_exp: number /* uint32 */;
  /**
   * Initial margin fraction in hundredths (as denominator),
   * eg. 10% = 1000
   */
  initial_margin: Fraction;
  /**
   * Maintenance margin fraction in hundredths (as denominator),
   * eg. 5% = 2000
   */
  maintenance_margin: Fraction;
  /**
   * Maker fee for the base fee tier, in 10^-6 fractions, negative means rebate.
   * Equal to the first entry of MakerFees.
   */
  maker_fee: Micros;
  /**
   * Taker fee for the base fee tier, in 10^-6 fractions.
   * Equal to the first entry of TakerFees.
   */
  taker_fee: Micros;
  /**
   * Maker fee for each fee tier, in 10^-6 fractions, indexed by tier.
   * The first entry is the base tier; higher tiers are progressively
   * discounted. An account's own fee tier is reported in its account state.
   */
  maker_fees?: Micros[];
  /**
   * Taker fee for each fee tier, in 10^-6 fractions, indexed by tier.
   */
  taker_fees?: Micros[];
  /**
   * Order recycling fee locked from the posting user's account, integer scaled by `Decimals` of the collateral token
   */
  recycle_fee: Amount;
  /**
   * Version of the exchange smart contract this market trades on, as
   * [major, minor, patch] - comparable element by element, oldest to newest.
   * It renders as "v1.<major>.<minor>.<patch>": the leading "v1" epoch is fixed
   * for the contract's lifetime and is not part of the array.
   * Identical across every market of the same exchange instance; reported per
   * market so the contract behind a market is visible where its configuration
   * is.
   * Empty while the version is not known, which is not an error condition -
   * treat it as "unspecified" rather than as an older release. Never partially
   * filled: either three elements or none.
   */
  contract_version?: number /* uint32 */[];
}
/**
 * Market state
 */
export interface MarketState {
  /**
   * Block/timestamp of the state
   */
  at: BlockTimestamp;
  /**
   * Oracle price, scaled by market `PriceDecimals`
   */
  orl: Price;
  /**
   * Mark price, scaled by market `PriceDecimals`
   */
  mrk: Price;
  /**
   * Last price, scaled by market `PriceDecimals`
   */
  lst: Price;
  /**
   * Mid price, scaled by market `PriceDecimals`
   */
  mid: Price;
  /**
   * Best bid price, scaled by market `PriceDecimals`
   */
  bid: Price;
  /**
   * Best ask price, scaled by market `PriceDecimals`
   */
  ask: Price;
  /**
   * Price 24h ago, scaled by market `PriceDecimals`
   */
  prv: Price;
  /**
   * Daily volume, scaled by market `SizeDecimals`
   */
  dv: Size;
  /**
   * Daily volume amount, integer scaled by `Decimals` of the collateral token
   */
  dva: Amount;
  /**
   * Open interest, scaled by market `SizeDecimals`
   */
  oi: Size;
  /**
   * TVL / collateral locked in open positions, integer scaled by `Decimals` of the collateral token
   */
  tvl: Amount;
}
/**
 * Market funding event
 */
export interface FundingEvent {
  /**
   * Block/timestamp the funding rate is applied at.
   * The exchange requires a funding event to be set shortly before the block
   * it applies to, so this is slightly ahead of the block the event was
   * published in, and the timestamp of the most recent event may be an
   * estimate until that block is reached.
   */
  at: BlockTimestamp;
  /**
   * Adjusted block number of this funding event,
   * when the new rate is actually applied. Same as `at.b`.
   */
  feb: number /* uint64 */;
  /**
   * Funding rate, 10^-6 fractions
   */
  rate: Micros;
  /**
   * Funding event index price, scaled by market `PriceDecimals`
   */
  idx: Price;
  /**
   * Funding payment per size lot, scaled by market `PriceDecimals`
   */
  ppl: SPrice;
  /**
   * Funding sum to calculate total position funding payments, scaled by market `PriceDecimals` and `FundingSumDivider`
   */
  sum: SPrice;
  /**
   * Scaling divider of the funding sum
   */
  div: number /* uint32 */;
}
/**
 * Market configuration update notification
 */
export interface MarketConfigUpdate extends MessageHeader {
  /**
   * Configuration data per market
   */
  d: { [key: MarketID]: MarketConfig | undefined };
}
/**
 * Market state update notification
 */
export interface MarketStateUpdate extends MessageHeader {
  /**
   * Statistics data per market
   */
  d: { [key: MarketID]: MarketState | undefined };
}
/**
 * Market funding rate update notification
 */
export interface MarketFundingUpdate extends MessageHeader {
  /**
   * Funding event per market
   */
  d: { [key: MarketID]: FundingEvent | undefined };
}

//////////
// source: market_data.go

/**
 * Side of the trade
 */
export type TradeSide = number /* uint8 */;
/**
 * Side of the trade.
 * Append-only, values can be renamed but never snould be removed
 */
export const TradeSideBuy: OrderType = 0 + 1;
/**
 * Side of the trade.
 * Append-only, values can be renamed but never snould be removed
 */
export const TradeSideSell: OrderType = 1 + 1;
/**
 * Single candlestick or arbitrary resolution as specified by the containing `CandleSeries`.
 * Uses floating-point number representation as is intended purely for
 * visualization purposes
 */
export interface Candle {
  /**
   * Opened at timestamp (ms)
   */
  t: number /* uint64 */;
  /**
   * Open, scaled by market `PriceDecimals`
   */
  o: Price;
  /**
   * Close, scaled by market `PriceDecimals`
   */
  c: Price;
  /**
   * High, scaled by market `PriceDecimals`
   */
  h: Price;
  /**
   * Low, scaled by market `PriceDecimals`
   */
  l: Price;
  /**
   * Total trading volume, in collateral token
   */
  v: Amount;
  /**
   * Number of trades
   */
  n: number /* uint32 */;
}
/**
 * Series of candles, can represent either snapshot or update, distinguished by `MsgType`.
 */
export interface CandleSeries extends MessageHeader {
  /**
   * Block/timestamp the series created at
   */
  at: BlockTimestamp;
  /**
   * Resolution of the candles (sec)
   */
  r: number /* uint32 */;
  /**
   * Continuous series of candles.
   * X last items for snapshot, for update there are up to two items:
   * * previous candle that was closed
   * * current/new candle that was updated
   * Ordered from older to newer.
   */
  d: Candle[];
}
/**
 * Maximum number of funding intervals a single market's funding history request
 * may cover, longer periods have to be requested in several parts
 */
export const MaxFundingEventsPerRequest = 1024;
/**
 * Maximum number of funding intervals a funding history request covering every
 * market may cover, an order of magnitude below `MaxFundingEventsPerRequest` as
 * the response carries that many events for each market of the exchange
 */
export const MaxAllMarketsFundingEventsPerRequest = 128;
/**
 * Series of funding events of a single market, ordered from older to newer.
 * A funding event is published for every funding interval the market has a
 * rate set for; an interval without one is absent from the series.
 */
export interface FundingSeries extends MessageHeader {
  /**
   * Block/timestamp of the most recent funding event in the series
   */
  at: BlockTimestamp;
  /**
   * ID of the market the series belongs to
   */
  m: MarketID;
  /**
   * Funding events within the requested period, ordered from older to newer
   */
  d: FundingEvent[];
}
/**
 * Funding events of every market, each series ordered from older to newer and
 * following the rules of `FundingSeries`. A market with no funding events yet
 * is absent from the response.
 */
export interface MarketFundingSeries extends MessageHeader {
  /**
   * Block/timestamp of the most recent funding event across all the series
   */
  at: BlockTimestamp;
  /**
   * Funding events within the requested period, per market ID
   */
  d: { [key: MarketID]: FundingEvent[] };
}
/**
 * Maximum number of price levels per side an L2 book request may ask for, and
 * the depth served when it does not ask for one. The deep tail of the book is
 * not served over REST at all - a client following the book subscribes to the
 * `order-book` stream instead
 */
export const MaxBookLevels = 100;
/**
 * L2 order book price level with price and size represented
 * as a scaled integer, corresponding number of decimal places is specified
 * by `MarketParams`
 */
export interface L2PriceLevel {
  /**
   * Price, scaled by market `PriceDecimals`
   */
  p: Price;
  /**
   * Size, scaled by market `SizeDecimals`
   */
  s: Size;
  /**
   * Number of orders, remove the price level if 0
   */
  o: number /* uint32 */;
}
/**
 * L2 order book snapshot or update, distinguished by `MsgType`
 */
export interface L2Book extends MessageHeader {
  /**
   * Block/timestamp of the snapshot/update
   */
  at: BlockTimestamp;
  /**
   * Bid price levels snapshot/update, ordered away from the spread
   */
  bid: L2PriceLevel[];
  /**
   * Ask price levels snapshot/update, ordered away from the spread
   */
  ask: L2PriceLevel[];
}
/**
 * Trade
 */
export interface Trade {
  /**
   * Block/timestamp/tx of the trade
   */
  at: BlockTxLogTimestamp;
  /**
   * Price, scaled by market `PriceDecimals`
   */
  p: Price;
  /**
   * Size, scaled by market `SizeDecimals`
   */
  s: Size;
  /**
   * Aggressive side of the trade
   */
  sd: TradeSide;
}
/**
 * Continuous series of trades, snapshot or update as per `MsgType`
 */
export interface TradeSeries extends MessageHeader {
  /**
   * Trades data, from older to newer
   */
  d: Trade[];
}

//////////
// source: message_type.go

/**
 * WebSocket API message types
 */
export type MessageType = number /* uint32 */;
/**
 * WebSocket API message types
 */
export const MsgTypeUnspecified: MessageType = 0;
/**
 * WebSocket API message types
 */
export const MsgTypePing: MessageType = 1;
/**
 * WebSocket API message types
 */
export const MsgTypePong: MessageType = 2;
/**
 * WebSocket API message types
 */
export const MsgTypeStatusResponse: MessageType = 3;
/**
 * WebSocket API message types
 */
export const MsgTypeAuthSignIn: MessageType = 4;
/**
 * WebSocket API message types
 */
export const MsgTypeSubscriptionRequest: MessageType = 5;
/**
 * WebSocket API message types
 */
export const MsgTypeSubscriptionResponse: MessageType = 6;
/**
 * WebSocket API message types
 */
export const MsgTypeGasPriceUpdate: MessageType = 7;
/**
 * WebSocket API message types
 */
export const MsgTypeMarketConfigUpdate: MessageType = 8;
/**
 * WebSocket API message types
 */
export const MsgTypeMarketStateUpdate: MessageType = 9;
/**
 * WebSocket API message types
 */
export const MsgTypeMarketFundingUpdate: MessageType = 10;
/**
 * WebSocket API message types
 */
export const MsgTypeCandlesSnapshot: MessageType = 11;
/**
 * WebSocket API message types
 */
export const MsgTypeCandlesUpdate: MessageType = 12;
/**
 * WebSocket API message types
 */
export const MsgTypeMarketFundingSnapshot: MessageType = 13;
/**
 * WebSocket API message types
 */
export const MsgTypeL2BookSnapshot: MessageType = 15;
/**
 * WebSocket API message types
 */
export const MsgTypeL2BookUpdate: MessageType = 16;
/**
 * WebSocket API message types
 */
export const MsgTypeTradesSnapshot: MessageType = 17;
/**
 * WebSocket API message types
 */
export const MsgTypeTradesUpdate: MessageType = 18;
/**
 * WebSocket API message types
 */
export const MsgTypeWalletSnapshot: MessageType = 19;
/**
 * WebSocket API message types
 */
export const MsgTypeWalletUpdate: MessageType = 20;
/**
 * WebSocket API message types
 */
export const MsgTypeAccountUpdate: MessageType = 21;
/**
 * WebSocket API message types
 */
export const MsgTypeOrderRequest: MessageType = 22;
/**
 * WebSocket API message types
 */
export const MsgTypeOrdersSnapshot: MessageType = 23;
/**
 * WebSocket API message types
 */
export const MsgTypeOrdersUpdate: MessageType = 24;
/**
 * WebSocket API message types
 */
export const MsgTypeFillsUpdate: MessageType = 25;
/**
 * WebSocket API message types
 */
export const MsgTypePositionsSnapshot: MessageType = 26;
/**
 * WebSocket API message types
 */
export const MsgTypePositionsUpdate: MessageType = 27;
/**
 * WebSocket API message types
 */
export const MsgTypeAccountStatsUpdate: MessageType = 28;
/**
 * WebSocket API message types
 */
export const MsgTypeApiKeySignIn: MessageType = 29;
/**
 * WebSocket API message types
 */
export const MsgTypeBatchOrderRequest: MessageType = 30;
/**
 * WebSocket API message types
 */
export const MsgTypeBatchStatusResponse: MessageType = 31;
/**
 * WebSocket API message types
 */
export const MsgTypeHeartbeat: MessageType = 100;

//////////
// source: oauth.go

export interface OAuthXUserNameResponse {
  user_name: string;
}

//////////
// source: profile.go

export const ReferralRefCodeKind = 'referral';
/**
 * MaxRefCodeInputLen is the maximum accepted length of a client-submitted ref
 * code string. It caps both base58 codes and custom aliases, which may contain
 * non-base58 characters; the profile service resolves aliases and validates
 * unknown codes. Kept in sync with the alias length cap enforced when an
 * operator creates an alias.
 */
export const MaxRefCodeInputLen = 32;
/**
 * Status of the referral
 */
export type ReferralStatus = number /* uint8 */;
/**
 * Status of the referral
 * Append-only, values can be renamed but never snould be removed
 */
export const ReferralStatusInactive: ReferralStatus = 0;
/**
 * Status of the referral
 * Append-only, values can be renamed but never snould be removed
 */
export const ReferralStatusPending: ReferralStatus = 1;
/**
 * Status of the referral
 * Append-only, values can be renamed but never snould be removed
 */
export const ReferralStatusActive: ReferralStatus = 2;
/**
 * Status of the referral
 * Append-only, values can be renamed but never snould be removed
 */
export const ReferralStatusExpired: ReferralStatus = 3;
/**
 * Status of the reward payout
 */
export type RewardPayoutStatus = number /* uint8 */;
/**
 * Status of the reward payout.
 * Append-only, values can be renamed but never should be removed.
 */
export const RewardPayoutStatusPending: RewardPayoutStatus = 0;
/**
 * Status of the reward payout.
 * Append-only, values can be renamed but never should be removed.
 */
export const RewardPayoutStatusProcessing: RewardPayoutStatus = 1;
/**
 * Status of the reward payout.
 * Append-only, values can be renamed but never should be removed.
 */
export const RewardPayoutStatusTransferred: RewardPayoutStatus = 2;
/**
 * Type of the profile boost
 */
export type BoostType = number /* uint8 */;
/**
 * Type of the profile boost. Mirrors the profile.BoostType proto enum.
 * Append-only, values can be renamed but never should be removed.
 */
export const BoostTypeUnspecified: BoostType = 0;
/**
 * Referrer 1st-tier reward boost (Fee Share L1).
 */
export const BoostTypeRewards1st: BoostType = 1;
/**
 * Referrer 2nd-tier reward boost (Fee Share L2).
 */
export const BoostTypeRewards2nd: BoostType = 2;
/**
 * Points-earning boost for the referrer.
 */
export const BoostTypePointsReferrer: BoostType = 3;
/**
 * Points-earning boost for the referral (referee).
 */
export const BoostTypePointsReferral: BoostType = 4;
/**
 * Trading fee rebate boost.
 */
export const BoostTypeRebates: BoostType = 5;
/**
 * Fee-tier boost: raises the volume-assigned fee tier by a number of rungs.
 * NOTE: for this type the boost value is a RUNG COUNT, not basis points.
 */
export const BoostTypeFeeTier: BoostType = 6;
/**
 * Manually granted fee tier: holds the profile on one fee tier for the
 * duration of the boost, whatever its trading volume earns.
 * NOTE: for this type the boost value is an ABSOLUTE TIER, not a rung count
 * and not basis points.
 */
export const BoostTypeFeeTierManual: BoostType = 7;
/**
 * Status of a builder payout
 */
export type BuilderPayoutStatus = number /* uint8 */;
/**
 * Nothing to settle for this epoch: the code earned no fees in it, charged
 * no fee, or was not registered when the epoch was aggregated.
 */
export const BuilderPayoutStatusPending: BuilderPayoutStatus = 0;
/**
 * The epoch is CLOSED AND ITS AMOUNT IS FINAL, and no matching transfer has
 * been observed yet. It does NOT mean a payment is under way: nothing in the
 * system knows whether a payment run has begun.
 */
export const BuilderPayoutStatusProcessing: BuilderPayoutStatus = 1;
/**
 * A transfer matching the amount owed landed after the epoch closed, so
 * txid/pts/pbl are set. This is matched by amount and time rather than
 * recorded against the epoch, so it is strong evidence and not a receipt: a
 * payment under another programme for a coincidentally equal amount can
 * satisfy it, and one transfer settling several epochs at once satisfies
 * none of them.
 */
export const BuilderPayoutStatusTransferred: BuilderPayoutStatus = 2;
/**
 * Incentive program a profile can be enrolled in
 */
export type ProgramType = number /* uint16 */;
/**
 * Type of the incentive program.
 * Append-only, values can be renamed but never should be removed.
 */
export const ProgramTypeUnspecified: ProgramType = 0;
/**
 * Partner program: raises referral reward and points boosts by volume tier.
 */
export const ProgramTypePartner: ProgramType = 1;
/**
 * Market making program.
 */
export const ProgramTypeMarketMaker: ProgramType = 2;
/**
 * Holder of one or more builder codes. A one-rung ladder: a wallet holds
 * codes or it does not, so the membership is always tier 1.
 */
export const ProgramTypeBuilder: ProgramType = 3;
/**
 * Referral code.
 */
export interface RefCode {
  /**
   * Code to share. Holds the custom alias instead of the base58 code when one
   * is configured (see IsAlias).
   */
  code: string;
  /**
   * True when Code holds a custom alias rather than the raw base58 ref code.
   */
  is_alias?: boolean;
  /**
   * Maximum number of profiles that can be created using this code.
   */
  limit?: number /* uint32 */;
  /**
   * Number of profiles already created using this code.
   */
  used?: number /* uint32 */;
  /**
   * Total trading volume generated by referred profiles, not including T2 referrals, (all time), CNS
   */
  volume?: Amount;
  /**
   * Ref code creation timestamp (ms)
   */
  created_at: number /* uint64 */;
}
/**
 * Profile contact info
 */
export interface ContactInfo {
  contact: string;
  x_challenge: string;
}
/**
 * Maximum allowed size of the profile settings JSON document, bytes.
 * Keep in sync with `PROFILE_SETTINGS_MAX_SIZE` in the Rust profile service.
 */
export const ProfileSettingsMaxSize = 16 * 1024;
/**
 * Arbitrary client-defined profile settings (opaque JSON object, private to the profile)
 */
export type Settings = { [key: string]: any };
/**
 * Announcements response view data
 */
export interface AnnouncementsResponse extends VersionHeader {
  active: Announcement[];
}
/**
 * Announcement data
 */
export interface Announcement {
  id: number /* uint32 */;
  title: string;
  content: string;
}
/**
 * Referrals view data
 */
export interface Referrals {
  /**
   * Block/timestamp of the last update
   */
  at: BlockTimestamp;
  /**
   * Number of Tier 1 referrals
   */
  t1_referrals: number /* uint32 */;
  /**
   * Number of Tier 2 referrals
   */
  t2_referrals: number /* uint32 */;
  /**
   * Total rewards from referrals, CNS: the sum of per-referral boost-aware
   * accumulated rewards (`tr`, all closed epochs) plus the live in-progress
   * estimate (`epoch_rewards`). Equals sum(d.tr) + epoch_rewards.
   */
  total_rewards: Amount;
  /**
   * Aggregate live in-progress-epoch reward estimate across all referrals
   * (sum of d.er), CNS. Included in total_rewards.
   */
  epoch_rewards: Amount;
  /**
   * Points earned from referral bonuses across all epochs: the share earned as
   * a referee plus the share earned as a referrer. The same figure as the
   * Points view's total_referral_bonus, and likewise a part of total_points
   * rather than an addition to it. Zero when the points service is unavailable.
   */
  total_referral_bonus: Amount;
  /**
   * Display-name alias; present only for joined partners
   */
  alias?: string;
  /**
   * Partner program tier ladder; omitted for non-partners / tier 0. The
   * eligibility volume gate is public and lives in /pub/context
   * (rewards.partner_min_volume).
   */
  partner_program?: PartnerProgramConfig;
  /**
   * Current assigned partner program tier; omitted for non-partners / tier 0
   */
  partner_tier?: number /* uint32 */;
  /**
   * Predicted next partner tier from current live referred volume; partner only
   */
  next_partner_tier?: number /* uint32 */;
  /**
   * Live referred volume (volume_threshold_leaderboard window) feeding the
   * next-tier prediction, CNS; partner only
   */
  cur_referred_volume?: Amount;
  /**
   * All-time referred volume, CNS; partner only
   */
  total_referred_volume?: Amount;
  /**
   * Profile referrals, from newest to oldest
   */
  d: Referral[];
}
/**
 * Partner program configuration.
 */
export interface PartnerProgramConfig {
  /**
   * Tier ladder, ordered by min_volume ascending (tier N is the Nth entry)
   */
  tiers: PartnerTierConfig[];
}
/**
 * PartnerTierConfig is a single partner-program tier as exposed publicly. Boost
 * fields are multipliers (bps, 10000 = 100%).
 */
export interface PartnerTierConfig {
  /**
   * Minimum referred volume to reach this tier, CNS
   */
  min_volume: Amount;
  /**
   * Points-earning boost for the referrer, bps
   */
  points_referrer_boost_bps: number /* uint32 */;
  /**
   * Points-earning boost for the referral (referee), bps
   */
  points_referral_boost_bps: number /* uint32 */;
  /**
   * Referral reward boost for 1st-level referrals (Fee Share L1), bps
   */
  referral_reward_1st_boost_bps: number /* uint32 */;
  /**
   * Referral reward boost for 2nd-level referrals (Fee Share L2), bps
   */
  referral_reward_2nd_boost_bps: number /* uint32 */;
}
/**
 * Referral details
 */
export interface Referral {
  /**
   * Referral wallet address
   */
  a: string;
  /**
   * Referral backend profile creation (not smart contract account creation) timestamp (ms)
   */
  cts: number /* uint64 */;
  /**
   * Referral status
   */
  s: ReferralStatus;
  /**
   * Total volume traded by the referral (not including T2 referrals, all time), CNS
   */
  tv: Amount;
  /**
   * Fees paid by the referral that reward sharing is computed on (not including T2
   * referrals, all time), CNS. This is the protocol fee only: a builder fee the
   * referral paid goes to that builder and earns no referral reward, so this can be
   * less than the total fees the referral was charged.
   */
  tf: Amount;
  /**
   * Total rewards earned from this referral, including T2 referrals (all
   * time, boost-aware), CNS. Sourced from the reward ledger (0 until the
   * first epoch is aggregated) plus the live in-progress-epoch estimate
   * (EpochRewards), matching the aggregate Referrals.TotalRewards.
   */
  tr: Amount;
  /**
   * Number of Tier 2 referrals
   */
  t2c: number /* uint32 */;
  /**
   * Total rewards earned from T2 referrals (all time, boost-aware, includes
   * the live epoch estimate T2EpochRewards), CNS
   */
  t2r: Amount;
  /**
   * Live in-progress-epoch reward estimate from this referral (t1+t2), CNS
   */
  er: Amount;
  /**
   * Live in-progress-epoch reward estimate from this referral's T2 referrals, CNS
   */
  t2er: Amount;
  /**
   * Referral memo
   */
  m: string;
}
/**
 * Referral memo update request/response
 */
export interface ReferralMemo {
  m: string;
}
/**
 * Alias (display name) get/set request/response
 */
export interface Alias {
  alias: string;
}
/**
 * Request body for linking the authenticated profile to a referrer via a ref code.
 */
export interface ReferredBy {
  /**
   * Ref code identifying the referrer
   */
  ref_code: string;
}
/**
 * Response body for linking the authenticated profile to a referrer via a ref code.
 */
export interface Referrer {
  /**
   * New referrer of the current profile
   */
  referrer: string;
}
/**
 * Rewards view data
 */
export interface Rewards {
  /**
   * Block/timestamp of the last update
   */
  at: BlockTimestamp;
  /**
   * Total reward payouts (all time), CNS
   */
  total_payouts: Amount;
  /**
   * Reward payouts, from newest to oldest
   */
  d: RewardPayout[];
}
/**
 * Reward payout details
 */
export interface RewardPayout {
  /**
   * Number of the rewards epoch, 1-based
   */
  e: number /* uint32 */;
  /**
   * Epoch starting timestamp (ms)
   */
  sts: number /* uint64 */;
  /**
   * Epoch duration (sec)
   */
  dur: number /* uint32 */;
  /**
   * Rewards to pay out
   */
  r: Amount;
  /**
   * Epoch rewards payout status
   */
  s: RewardPayoutStatus;
  /**
   * Payout transaction hash, set when status is transferred.
   */
  txid?: string;
  /**
   * Payout timestamp (ms), set when status is transferred.
   */
  pts?: number /* uint64 */;
  /**
   * Block number the payout was mined at, set when status is transferred.
   */
  pbl?: number /* uint64 */;
}
/**
 * Builder dashboard headline figures: one row per builder code registered to the
 * calling wallet, over all time.
 * There is deliberately no totals row across a wallet's codes. Volume, fees and
 * trades are additive, but traders is not — an account routing through two of the
 * wallet's codes is one trader, not two — so a totals row would be right in four
 * columns and wrong in one.
 */
export interface BuilderStats {
  /**
   * Block/timestamp the builder leaderboard these figures come from is current at
   */
  at: BlockTimestamp;
  /**
   * One entry per registered code, ordered by code. Empty when the wallet holds
   * none.
   */
  d: BuilderStatsEntry[];
}
/**
 * All-time figures for one builder code.
 * A code with no attributed flow is reported as a zero row ranked last, rather
 * than omitted: a registered code that has earned nothing is a meaningful state.
 */
export interface BuilderStatsEntry extends BuilderLeaderboardEntry {
  /**
   * Whether the code is still live. A retired code keeps reporting its history
   * but can no longer be enrolled against, and earns nothing in epochs closed
   * while it is retired.
   */
  en: boolean;
}
/**
 * Builder payout history: per-epoch earnings for every builder code the calling
 * wallet holds.
 * A code's whole history travels with the code: re-point a code to a new address
 * and every epoch it has ever earned is reported there, and no longer at the old
 * one.
 */
export interface BuilderPayouts {
  /**
   * Block/timestamp of the most recent settlement or aggregation carried by the
   * response
   */
  at: BlockTimestamp;
  /**
   * Payout rows for CLOSED epochs only, newest epoch first, then by code.
   * One row per epoch PER CODE, not one row per epoch: a wallet holding three
   * codes gets three consecutive rows for each epoch, one per code, and the
   * epoch's own total is their sum. Group by `e` before displaying an epoch.
   * The epoch in flight has no row here, by construction: it is still running.
   * Its window comes from `builder_epoch_starts_at` / `builder_epoch_duration_sec`
   * on the rewards configuration, as every other epoched program's does, and what
   * a code has accrued into it is this endpoint's newest `tf` for that code
   * subtracted from the code's `bf` on the builder stats endpoint, floored at
   * zero. Both are gross of the payout share; applying `builder_payout_share_bps`
   * is multiply-then-divide, truncating toward zero, matching what the payout
   * pipeline does.
   */
  d: BuilderPayout[];
}
/**
 * A builder payout epoch's identity and window
 */
export interface BuilderEpoch {
  /**
   * Number of the builder payout epoch, 1-based
   */
  e: number /* uint32 */;
  /**
   * Epoch starting timestamp (ms)
   */
  sts: number /* uint64 */;
  /**
   * Epoch duration (sec)
   */
  dur: number /* uint32 */;
}
/**
 * One builder code's earnings over one closed epoch.
 * An epoch a held code earned nothing in is reported as a zero row, not omitted:
 * "this code earned nothing in week 3" and "week 3 is missing" are different
 * statements.
 */
export interface BuilderPayout extends BuilderEpoch {
  /**
   * Builder code
   */
  id: number /* uint32 */;
  /**
   * Volume attributed to this code during the epoch, CNS
   */
  v: Amount;
  /**
   * Builder fees this code accrued during the epoch, CNS, GROSS — before the
   * configured payout share is applied
   */
  f: Amount;
  /**
   * Amount owed for the epoch, CNS — the accrued fees after the payout share
   */
  r: Amount;
  /**
   * Volume attributed to this code through the END of this epoch, CNS: a running
   * total, not the epoch's own figure
   */
  tv: Amount;
  /**
   * Builder fees accrued through the END of this epoch, CNS, gross: a running
   * total, and the baseline the in-flight epoch's accrual is measured from
   */
  tf: Amount;
  /**
   * Settlement status for the epoch
   */
  s: BuilderPayoutStatus;
  /**
   * Payout transaction hash, set when status is transferred.
   */
  txid?: string;
  /**
   * Payout timestamp (ms), set when status is transferred.
   */
  pts?: number /* uint64 */;
  /**
   * Block number the payout was mined at, set when status is transferred.
   */
  pbl?: number /* uint64 */;
}
/**
 * Points view data
 */
export interface Points {
  /**
   * Block/timestamp of the last update
   */
  at: BlockTimestamp;
  /**
   * Total Points for the profile
   */
  total_points: Amount;
  /**
   * Part of TotalPoints that came from referral bonuses
   */
  total_referral_bonus: Amount;
  /**
   * Total Points rank of the profile
   */
  total_rank: number /* uint32 */;
  /**
   * Last epoch Points emission
   */
  last_epoch_emission: Amount;
  /**
   * Points rewards epochs, from newest to oldest
   */
  d: PointsEpoch[];
}
/**
 * Points reward epoch details
 */
export interface PointsEpoch {
  /**
   * Number of the Points epoch, 0 = pre-season
   */
  e: number /* uint32 */;
  /**
   * Epoch starting timestamp (ms)
   */
  sts: number /* uint64 */;
  /**
   * Epoch duration (sec)
   */
  dur: number /* uint32 */;
  /**
   * Rank in this epoch
   */
  r: number /* uint32 */;
  /**
   * Points earned in this epoch
   */
  p: Amount;
  /**
   * Part of Points that came from referral bonuses: the share earned as a
   * referee plus the share earned as a referrer
   */
  rb: Amount;
  /**
   * Epoch badges
   */
  b: string[];
}
/**
 * MPoints view data
 */
export interface MPoints {
  /**
   * Block/timestamp of the last update
   */
  at: BlockTimestamp;
  /**
   * Total MPoints for the profile
   */
  total_mpoints: Amount;
  /**
   * Total MPoints rank of the profile
   */
  total_rank: number /* uint32 */;
  /**
   * MPoints rewards epochs, from newest to oldest
   */
  d: MPointsEpoch[];
}
/**
 * MPoints reward epoch details
 */
export interface MPointsEpoch {
  /**
   * Number of the MPoints epoch, 1-based
   */
  e: number /* uint32 */;
  /**
   * Epoch starting timestamp (ms)
   */
  sts: number /* uint64 */;
  /**
   * Epoch duration (sec)
   */
  dur: number /* uint32 */;
  /**
   * Rank in this epoch
   */
  r: number /* uint32 */;
  /**
   * MPoints earned in this epoch (U256, scaled by 10^6)
   */
  mp: Amount;
}
/**
 * Boosts view data: the currently active winning boost per type.
 */
export interface Boosts {
  /**
   * Active winning boosts, at most one per type
   */
  d: Boost[];
}
/**
 * Boost details
 */
export interface Boost {
  /**
   * Externally provided boost identifier, unique per profile
   */
  id: number /* uint64 */;
  /**
   * Boost type (0 = rewards, 1 = points, 2 = rebates)
   */
  bt: BoostType;
  /**
   * Boost multiplier, basis points
   * 10000 = 1x (no boost), 20000 = 2x boost
   * For BoostTypeFeeTier this is a RUNG COUNT instead: the number of fee-tier
   * steps the boost adds, capped at the top tier; for BoostTypeFeeTierManual
   * it is the granted fee tier itself.
   */
  bps: number /* uint32 */;
  /**
   * Boost start timestamp (ms), 0 if open-ended start
   */
  sts?: number /* uint64 */;
  /**
   * Boost end timestamp (ms), 0 if open-ended
   */
  ets?: number /* uint64 */;
  /**
   * Boost creation timestamp (ms)
   */
  cts: number /* uint64 */;
}
/**
 * A profile's participation in one incentive program.
 */
export interface ProgramMembership {
  /**
   * Current tier on the program's tier ladder, counting from 1.
   */
  t: number /* uint32 */;
}
/**
 * Programs view data: the incentive programs the profile is enrolled in.
 */
export interface Programs {
  /**
   * Membership keyed by program type, empty when the profile is enrolled in
   * none. A program applied for but not (yet) granted is not reported here.
   */
  pt: { [key: ProgramType]: ProgramMembership };
}

//////////
// source: streams.go

/**
 * Request to subscribe/unsubscribe multiple streams within
 * the current WebSocket connection
 */
export interface SubscriptionRequest extends MessageHeader {
  /**
   * Stream subscription operations to perform
   */
  subs: SubscriptionOp[];
}
/**
 * Streams subscription response
 */
export interface SubscriptionResponse extends MessageHeader {
  /**
   * Stream subscription operation results
   */
  subs: SubscriptionStatus[];
}
/**
 * Stream subscription operation
 */
export interface SubscriptionOp {
  /**
   * Name of the stream, supported streams:
   * * heartbeat@<chain_id>
   * * gas-stats@<chain_id>
   * * market-config@<chain_id>
   * * market-state@<chain_id>
   * * funding@<chain_id>
   * * candles@<market_id>*<resolution>, (resolutions: [60, 300, 900, 1800, 3600, 7200, 14400, 28800, 43200, 86400] /* [1m,5m,15m,30m,1h,2h,4h,8h,12h,1d] *\/)
   * * order-book@<market_id>
   * * trades@<market_id>
   * Only `heartbeat` and `gas-stats` streams guarantee continuous `SeqNo` sequences.
   */
  stream: string;
  /**
   * True = subscribe, false = unsubscribe
   */
  subscribe: boolean;
}
/**
 * Stream subscription status
 */
export interface SubscriptionStatus {
  /**
   * Name of the stream
   */
  stream: string;
  /**
   * ID of the subscription, will be provided with each message
   * sent by this subscription
   */
  sid?: number /* uint32 */;
  /**
   * Status of the subscription operation
   */
  status: Status;
}

//////////
// source: trading.go

/**
 * Type of the order
 */
export type OrderType = number /* uint8 */;
/**
 * Type of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderTypeUnspecified: OrderType = 0;
/**
 * Type of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderTypeOpenLong: OrderType = 1;
/**
 * Type of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderTypeOpenShort: OrderType = 2;
/**
 * Type of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderTypeCloseLong: OrderType = 3;
/**
 * Type of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderTypeCloseShort: OrderType = 4;
/**
 * Type of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderTypeCancel: OrderType = 5;
/**
 * Type of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderTypeIncreasePositionCollateral: OrderType = 6;
/**
 * Type of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderTypeChange: OrderType = 7;
/**
 * Order flags
 */
export type OrderFlags = number /* uint8 */;
/**
 * Order flags.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderFlagGoodTillCancel: OrderFlags = 0;
/**
 * Order flags.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderFlagPostOnly = 1 << (1 - 1);
/**
 * Order flags.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderFlagFillOrKill = 1 << (2 - 1);
/**
 * Order flags.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderFlagImmediateOrCancel = 1 << (3 - 1);
/**
 * Order trigger price condition
 */
export type TriggerPriceCondition = number /* uint8 */;
export const TriggerPriceConditionUnspecified: TriggerPriceCondition = 0;
export const TriggerPriceConditionGTELast: TriggerPriceCondition = 1;
export const TriggerPriceConditionLTELast: TriggerPriceCondition = 2;
export const TriggerPriceConditionGTEMark: TriggerPriceCondition = 3;
export const TriggerPriceConditionLTEMark: TriggerPriceCondition = 4;
/**
 * Status of the order
 */
export type OrderStatus = number /* uint8 */;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusUnspecified: OrderStatus = 0;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusPending: OrderStatus = 1;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusOpen: OrderStatus = 2;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusPartiallyFilled: OrderStatus = 3;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusFilled: OrderStatus = 4;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusCanceled: OrderStatus = 5;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusExpired: OrderStatus = 6;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusFailed: OrderStatus = 7;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusUntriggered: OrderStatus = 8;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusTriggered: OrderStatus = 9;
/**
 * Status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusExecuted: OrderStatus = 10;
/**
 * Reason for the status of the order
 */
export type OrderStatusReason = number /* uint8 */;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonUnspecified: OrderStatusReason = 0;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonAmountExceedsAvailableBalance: OrderStatusReason = 1;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonAccountFrozen: OrderStatusReason = 2;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonCancelExistingInvalidCloseOrders: OrderStatusReason = 3;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonCantChangeCloseOrder: OrderStatusReason = 4;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonChangeExpiredOrderNeedsNewExpiry: OrderStatusReason = 5;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonClearingExpiredOrder: OrderStatusReason = 6;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonClearingFrozenAccountOrder: OrderStatusReason = 7;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonClearingInvalidCloseOrder: OrderStatusReason = 8;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonClearingSelfMatchingOrder: OrderStatusReason = 9;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonCloseOrderExceedsPosition: OrderStatusReason = 10;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonCloseOrderPositionMismatch: OrderStatusReason = 11;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonContractNotOperational: OrderStatusReason = 12;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonCrossesBook: OrderStatusReason = 13;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonExceedsLastExecutionBlock: OrderStatusReason = 14;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonForwardingReverted: OrderStatusReason = 15;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonImmediateOrCancelExecuted: OrderStatusReason = 16;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonImmediateOrderUnderMinimum: OrderStatusReason = 17;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonInsuficientFundsForRecycleFee: OrderStatusReason = 18;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonInvalidAccountFrozenOrder: OrderStatusReason = 19;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonInvalidExpiryBlock: OrderStatusReason = 20;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonInvalidOrderId: OrderStatusReason = 21;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonMakerOrderFilled: OrderStatusReason = 22;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonMakerOrderSettlementFailed: OrderStatusReason = 23;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonMaximumAccountOrders: OrderStatusReason = 24;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonMaxMatchesReached: OrderStatusReason = 25;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonNoOp: OrderStatusReason = 26;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderBookFull: OrderStatusReason = 27;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderCancelled: OrderStatusReason = 28;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderCancelledByAdmin: OrderStatusReason = 29;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderCancelledByLiquidator: OrderStatusReason = 30;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderChanged: OrderStatusReason = 31;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderDescIdTooLow: OrderStatusReason = 32;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderDoesNotExist: OrderStatusReason = 33;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderForwardingNotAllowed: OrderStatusReason = 34;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderPlaced: OrderStatusReason = 35;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderPostFailed: OrderStatusReason = 36;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderSettlementImpliesInsolvent: OrderStatusReason = 37;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderSizeExceedsAvailableSize: OrderStatusReason = 38;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonPostOrderUnderMinimum: OrderStatusReason = 39;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonPriceOutOfRange: OrderStatusReason = 40;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonRecycleBalanceInsufficientSevere: OrderStatusReason = 41;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonSizeOutOfRange: OrderStatusReason = 42;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTakerOrderFilled: OrderStatusReason = 43;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTakerOrderSettlementFailed: OrderStatusReason = 44;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonUnableToCancelOrder: OrderStatusReason = 45;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonUnmatchedLotRemainsInFillOrKill: OrderStatusReason = 46;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonUnspecifiedCollateral: OrderStatusReason = 47;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonUnspecifiedPrice: OrderStatusReason = 48;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonUnspecifiedSize: OrderStatusReason = 49;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonWrongAccountForOrder: OrderStatusReason = 50;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonWrongChainForOrder: OrderStatusReason = 51;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonWrongMarketForOrder: OrderStatusReason = 52;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonPerpetualInsolvent: OrderStatusReason = 53;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTriggered: OrderStatusReason = 54;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonInvalidAmount: OrderStatusReason = 55;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonInvalidFlags: OrderStatusReason = 56;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonInvalidTriggerOrder: OrderStatusReason = 57;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonWrongTriggerPosition: OrderStatusReason = 58;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTriggerDescIdTooLow: OrderStatusReason = 59;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTriggerOrderRequest: OrderStatusReason = 60;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonValueExceedsMaximum: OrderStatusReason = 61;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonClearingRemainingOrderLockBeyondBalance: OrderStatusReason = 62;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonPriceSetDuringTriggerExec: OrderStatusReason = 63;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTriggeredExecutionAttemptsExhausted: OrderStatusReason = 64;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTriggeredOrderExecuted: OrderStatusReason = 65;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTriggeredOrderPartiallyFilled: OrderStatusReason = 66;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTriggeredOrderExpired: OrderStatusReason = 67;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonTriggeredOrderRecoverableFailure: OrderStatusReason = 68;
/**
 * Reason for the status of the order.
 * Append-only, values can be renamed but never snould be removed
 */
export const OrderStatusReasonOrderExtensionRejected: OrderStatusReason = 69;
/**
 * Detail behind an order that failed to post or settle
 */
export type OrderFailureReason = number /* uint8 */;
/**
 * The order did not fail, or failed before the exchange evaluated it.
 */
export const OrderFailureReasonUnspecified: OrderFailureReason = 0;
/**
 * Account balance is below the collateral, negative-PnL collateral and fee
 * the new position requires.
 */
export const OrderFailureReasonInsufficientBalance: OrderFailureReason = 1;
/**
 * Available collateral is below what increasing the existing position requires.
 */
export const OrderFailureReasonInsufficientCollateralIncrease: OrderFailureReason = 2;
/**
 * Available collateral is below what inverting the existing position requires.
 */
export const OrderFailureReasonInsufficientCollateralInvert: OrderFailureReason = 3;
/**
 * A close order was evaluated against a position that does not exist.
 */
export const OrderFailureReasonNoPositionToClose: OrderFailureReason = 4;
/**
 * Settling the order at this price would leave the perpetual insolvent. An
 * order can rest on the book in this state, so it is only ever a settlement
 * failure, never a posting one.
 */
export const OrderFailureReasonPerpetualSolvency: OrderFailureReason = 5;
/**
 * Closing or decreasing the position would realise a negative position value.
 */
export const OrderFailureReasonNegativePositionValue: OrderFailureReason = 6;
/**
 * No fresh reference price was available to value the order against.
 */
export const OrderFailureReasonReferencePriceStale: OrderFailureReason = 7;
/**
 * The negative PnL the order would have to collateralize against the mark
 * price exceeds the `maxNegPnlCollatBPS` limit the order was placed with.
 */
export const OrderFailureReasonExceedsMaxNegPnlCollat: OrderFailureReason = 8;
/**
 * The order failed for a reason this API version does not publish yet.
 */
export const OrderFailureReasonOther: OrderFailureReason = 9;
/**
 * Side of liquidity
 */
export type LiquiditySide = number /* uint8 */;
/**
 * Side of liquidity.
 * Append-only, values can be renamed but never snould be removed
 */
export const LiquiditySideUnspecified: LiquiditySide = 0;
/**
 * Side of liquidity.
 * Append-only, values can be renamed but never snould be removed
 */
export const LiquiditySideMaker: LiquiditySide = 1;
/**
 * Side of liquidity.
 * Append-only, values can be renamed but never snould be removed
 */
export const LiquiditySideTaker: LiquiditySide = 2;
/**
 * Type of the position
 */
export type PositionType = number /* uint8 */;
/**
 * Type of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionTypeUnspecified: PositionType = 0;
/**
 * Type of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionTypeLong: PositionType = 1;
/**
 * Type of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionTypeShort: PositionType = 2;
/**
 * Status of the position
 */
export type PositionStatus = number /* uint8 */;
/**
 * Status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusUnspecified: PositionStatus = 0;
/**
 * Status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusOpen: PositionStatus = 1;
/**
 * Status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusClosed: PositionStatus = 2;
/**
 * Status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusLiquidated: PositionStatus = 3;
/**
 * Status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusDeleveraged: PositionStatus = 4;
/**
 * Status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusUnwound: PositionStatus = 5;
/**
 * Status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusFailed: PositionStatus = 6;
/**
 * Reason for the status of the position
 */
export type PositionStatusReason = number /* uint8 */;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonUnspecified: PositionStatusReason = 0;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonBankruptcyPricePreventsDeleverage: PositionStatusReason = 1;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonCantDeleverageAgainstOpposingPositions: PositionStatusReason = 2;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonCantBuyToLiquidate: PositionStatusReason = 3;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonCantLiquidatePosAboveMMR: PositionStatusReason = 4;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonDeleveragePositionListEmpty: PositionStatusReason = 5;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonIncreasePositionCollateral: PositionStatusReason = 6;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonInsolventPositionCannotBeForcedClose: PositionStatusReason = 7;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonInsufficientFundsToDecCollateral: PositionStatusReason = 8;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const Deprecated_PositionStatusReasonInsurancePaymentForLiquidation: PositionStatusReason = 9;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonInsurancePaymentForSettlement: PositionStatusReason = 10;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonInvalidBankruptcyPrice: PositionStatusReason = 11;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonInvalidLiquidationPrice: PositionStatusReason = 12;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionClosed: PositionStatusReason = 13;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionDecreased: PositionStatusReason = 14;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionDeleveraged: PositionStatusReason = 15;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionDoesNotExist: PositionStatusReason = 16;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionIncreased: PositionStatusReason = 17;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionInverted: PositionStatusReason = 18;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionLiquidated: PositionStatusReason = 19;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionLiquidationCredit: PositionStatusReason = 20;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionOpened: PositionStatusReason = 21;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionUnwound: PositionStatusReason = 22;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonUnwindInsufficientBalance: PositionStatusReason = 23;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonBorrowMarginNotMetAfterDecCollateral: PositionStatusReason = 24;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonBuyToLiquidateSlippageExceeded: PositionStatusReason = 25;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonCannotAdjustEntryPriceToDecCollateral: PositionStatusReason = 26;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonDeprecated1: PositionStatusReason = 27;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonDeprecated2: PositionStatusReason = 28;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonCollateralDecreaseDeclined: PositionStatusReason = 29;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonCollateralDecreaseRequested: PositionStatusReason = 30;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonCollateralDecreaseRequestCancelled: PositionStatusReason = 31;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonCollateralDecreaseRequestExpired: PositionStatusReason = 32;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonDecreaseCollateralBeyondMarkPrice: PositionStatusReason = 33;
/**
 * Reason for the status of the position.
 * Append-only, values can be renamed but never snould be removed
 */
export const PositionStatusReasonPositionCollateralDecreased: PositionStatusReason = 34;
/**
 * Order placement, change or cancellation, as the client describes it.
 * The same description on both transports: carried by `OrderRequest` over the
 * WebSocket connection, and posted on its own -- without the message header -- to
 * the order submission endpoint.
 * For requests with the same RequestID, offers "at-most once" delivery semantics -
 * it is guaranteed that the order will be executed at most once.
 * Client side is responsible for retries and should follow the following rules:
 *  1. Retry with original RequestID:
 *     a. Before receiving any status update for the original request
 *     b. Before `LastExecBlock` expiration
 *  2. Retry with new RequestID only when:
 *     a. Failure status received for the original request
 *     b. Current known block (eg. `Heartbeat.SeqNo`) is greater or equal to `LastExecBlock` of the original
 *     request and no status updates were received - only if all block updates / heartbeats
 *     after order posting were observed (i.e. there were no reconnections)
 * Request IDs of one account must not decrease: a request ID below the last one
 * accepted for the account is rejected. Repeating a request ID is the retry
 * mechanism above and stays accepted.
 */
export interface OrderSpec {
  /**
   * Request ID - idempotency key, unique identifier (per account) for
   * this order operation.
   * Strictly increasing - each next request ID should be greater than
   * the previous one.
   */
  rq: RequestID;
  /**
   * Market to post the command to
   */
  mkt: MarketID;
  /**
   * Account the order belongs to
   */
  acc: AccountID;
  /**
   * ID of the order to change or cancel, skip for new orders
   */
  oid?: OrderID;
  /**
   * Type of the order/request
   */
  t: OrderType;
  /**
   * Order limit price, zero for market, scaled by market `PriceDecimals`
   */
  p?: Price;
  /**
   * Order size, scaled by market `SizeDecimals`
   */
  s: Size;
  /**
   * Amount to increase position collateral by,
   * only for `OrderTypeIncreasePositionCollateral`
   */
  a?: Amount;
  /**
   * Maximum market order price slippage in basis points,
   * zero to use default value from `order_max_market_slippage_bps`
   */
  ms?: number /* uint32 */;
  /**
   * Maximum amount of unrealized negative PnL to collateralize when this order is
   * matched and filled, in basis points of the position notional. A fill that
   * would require collateralizing more than this is rejected with
   * `OrderFailureReasonExceedsMaxNegPnlCollat` rather than filled. Values above
   * 10000 (=100% of the notional) are meaningful, since the limit applies to the
   * notional of the whole resulting position, not just this order.
   * Range 0..65535, where an explicit 0 refuses any fill that would collateralize
   * negative PnL at all. Omit the field entirely - as opposed to sending 0 - to
   * take the market default from `order_max_neg_pnl_collat_bps`.
   */
  mnp?: number /* uint32 */;
  /**
   * Block number the order good till, GTC if unspecified.
   * Ignored for trigger orders.
   */
  tif?: number /* uint64 */;
  /**
   * Order flags, use bitwise OR to set multiple.
   */
  fl: OrderFlags;
  /**
   * Trigger price for stop loss/take profit order (=trigger order).
   * If specified, order will not be posted until the market last price
   * crosses this trigger price.
   */
  tp?: Price;
  /**
   * Condition on the trigger price.
   */
  tpc?: TriggerPriceCondition;
  /**
   * Order request triggering the placement of trigger order.
   * If specified:
   * - when the corresponding request leads to a trade: this trigger order gets activated
   * - when the corresponding request fails: this trigger order gets cancelled
   * - when the correspondong request places an order: this trigger order links to that order
   * - when linked order gets filled: this trigger order gets activated
   * - when linked order gets cancelled: this trigger order gets cancelled
   */
  tr?: RequestID;
  /**
   * Position the trigger order is linked to.
   * If specified, this trigger order will be cancelled when position is closed or inverted.
   */
  lp?: PositionID;
  /**
   * Leverage [1..`InitialMargin`], in hundredths
   */
  lv: number /* uint32 */;
  /**
   * Last block number this order can be executed at.
   * Should not exceed `head_block_number` + `order_ttl_blocks`.
   */
  lb: number /* uint64 */;
  /**
   * Builder fee for this order, in hundred-thousandths (1 = 0.1 bps), charged in
   * addition to the protocol fee and attributed to the builder code of the
   * authenticating API key.
   * Requires a builder-bound key, and must not exceed that key's registered maximum
   * (see `max_builder_fee_per_100k`); a higher value is rejected, not reduced.
   * Charged only on the size that opens or increases a position — closing or
   * reducing incurs no builder fee.
   * The builder code itself is taken from the authenticating key and cannot be set
   * per order. Omitting this field is not an error: the order executes with no
   * builder fee.
   */
  bf?: number /* uint32 */;
}
/**
 * Order posting/cancellation request.
 * MsgType = MsgTypeOrderRequest
 * The WebSocket framing of an `OrderSpec`, whose rules on request IDs, retries and
 * at-most-once execution apply here.
 * For each RequestID, multiple `Order` messages with status updates can be received.
 * Client side is responsible for deduplication of these messages, processing only:
 * 	a. The first failure message if all received messages are failures (`OrderStatusFailed`)
 * 	b. The first non-failure message received (OrderStatusOpen, OrderStatusPartiallyFilled,
 * 		OrderStatusFilled, OrderStatusCanceled, OrderStatusUntriggered, OrderStatusTriggered,
 * 		OrderStatusExecuted)
 */
export interface OrderRequest extends MessageHeader, OrderSpec {}
/**
 * Batch of order posting/cancellation requests.
 * MsgType = MsgTypeBatchOrderRequest
 * Carries several `OrderSpec`s in one request, each with the meaning it has on
 * its own: the rules on request IDs, retries and at-most-once execution are
 * per order, not per batch. The orders may name different markets and different
 * accounts of the calling wallet.
 * This is what the order submission endpoint accepts, and the way to submit
 * several orders at once with a single round trip of latency. The orders are
 * handed to the exchange in the order they are listed, so a batch that cancels
 * one order and places another is applied in that sequence.
 * A batch is not a transaction: each order is validated and forwarded on its
 * own, and the answer is a `BatchStatusResponse` carrying one status per order,
 * in the same order. Some orders being refused while the rest are accepted is an
 * ordinary outcome that has to be handled -- there is no rollback of the ones
 * that went through.
 */
export interface BatchOrderRequest extends MessageHeader {
  /**
   * Orders to submit, in the order they should reach the exchange, at least one
   * and at most 100 of them. Each order counts against the request rate limit
   * as a request of its own would, so batching saves round trips rather than
   * raising the rate at which orders may be submitted.
   */
  d: OrderSpec[];
}
/**
 * Order state snapshot/update
 */
export interface Order {
  /**
   * Block/timestamp/tx of this update
   */
  at: BlockTxLogTimestamp;
  /**
   * Block/timestamp/tx the order was created at
   */
  c: BlockTxTimestamp;
  /**
   * ID of the request created/updated the order
   */
  rq: RequestID;
  /**
   * Market the order belongs to
   */
  mkt: MarketID;
  /**
   * Account the order belongs to
   */
  acc: AccountID;
  /**
   * ID of the order, unique within market
   */
  oid: OrderID;
  /**
   * ID of the order at the smart contract, non-unique
   * and should be used only for direct cancellation
   */
  scid: OrderID;
  /**
   * Status of the order
   */
  st: OrderStatus;
  /**
   * Reason for the status of the order
   */
  sr: OrderStatusReason;
  /**
   * Detail behind `sr` when the order failed to post or settle
   */
  fr?: OrderFailureReason;
  /**
   * Type of the order
   */
  t: OrderType;
  /**
   * If order should be removed from open orders (updates only)
   */
  r?: boolean;
  /**
   * Order limit price, zero for market, scaled by market `PriceDecimals`
   */
  p?: Price;
  /**
   * Original size of the order, scaled by market `SizeDecimals`
   */
  os: Size;
  /**
   * Size-weighted average filling price, scaled by market `PriceDecimals`
   */
  fp: Price;
  /**
   * Filled size of the order, scaled by market `SizeDecimals`
   */
  fs: Size;
  /**
   * Total fee paid, scaled by `Decimals` of the `CollateralToken` of the `Instance`.
   * Includes `bfa`, so this is the full amount charged for the order.
   */
  f: Amount;
  /**
   * Builder-fee portion of `f`, in the same units. Zero unless the order was placed
   * with a builder-bound API key that requested a fee; `f` minus this is the
   * protocol fee.
   */
  bfa?: Amount;
  /**
   * Block number the order good till, GTC if unspecified
   */
  tif?: number /* uint64 */;
  /**
   * Order flags
   */
  fl: OrderFlags;
  /**
   * Trigger price for stop loss/take profit order, scaled by market `PriceDecimals`
   */
  tp?: Price;
  /**
   * Condition on the trigger price.
   */
  tpc?: TriggerPriceCondition;
  /**
   * Position the trigger order is linked to.
   * If specified, this trigger order will be cancelled when position is closed or inverted.
   */
  lp?: PositionID;
  /**
   * Maximum number of matches allowed
   */
  mm: number /* uint32 */;
  /**
   * Leverage, in hundredths
   */
  lv: number /* uint32 */;
  /**
   * Maximum amount of unrealized negative PnL to collateralize when this order is
   * matched and filled, in basis points of the position notional. Reflects the
   * market default when the order did not request a value of its own.
   */
  mnp: number /* uint32 */;
}
/**
 * Wallet active orders state snapshot/update, depending on `MsgType`
 */
export interface WalletOrders extends MessageHeader {
  /**
   * Block/timestamp of the snapshot/update
   */
  at: BlockTimestamp;
  /**
   * Order data, from older to newer
   */
  d: Order[];
}
/**
 * Page of historical order data
 */
export interface OrderHistoryPage {
  /**
   * Order data, from newer to older
   */
  d: Order[];
  /**
   * Next page cursor
   */
  np: string;
}
/**
 * Order fill
 */
export interface Fill {
  /**
   * Block/timestamp/tx of the fill
   */
  at: BlockTxLogTimestamp;
  /**
   * Market the filled order belongs to
   */
  mkt: MarketID;
  /**
   * Account the filled order belongs to
   */
  acc: AccountID;
  /**
   * ID of the filled order
   */
  oid: OrderID;
  /**
   * Type of the order
   */
  t: OrderType;
  /**
   * Liquidity side of the fill
   */
  l: LiquiditySide;
  /**
   * Fill price, scaled by market `PriceDecimals`
   */
  p?: Price;
  /**
   * Filled size, scaled by market `SizeDecimals`
   */
  s: Size;
  /**
   * Total fee paid or rebate received, scaled by `Decimals` of the `CollateralToken`
   * of the `Instance`. Includes `bfa`, so this is the full amount charged for the
   * fill.
   */
  f: Amount;
  /**
   * Builder-fee portion of `f`, in the same units. Zero unless the filled order was
   * placed with a builder-bound API key that requested a fee; `f` minus this is the
   * protocol fee. Fills that only close or reduce a position carry no builder fee.
   */
  bfa?: Amount;
}
/**
 * Wallet order fills, updates only
 */
export interface WalletFills extends MessageHeader {
  /**
   * Block/timestamp of the snapshot/update
   */
  at: BlockTimestamp;
  /**
   * Fills, from older to newer
   */
  d: Fill[];
}
/**
 * Page of historical fill data
 */
export interface FillHistoryPage {
  /**
   * Fills, from newer to older
   */
  d: Fill[];
  /**
   * Next page cursor
   */
  np: string;
}
/**
 * Position snapshot, or a single event from the position's history.
 * In an event, the amount fields report what THAT event moved rather than the
 * position's standing state: `Collateral` is the signed change, `Size` and `Fees`
 * are the magnitude of theirs, and `DeltaPnL`/`PremiumPnL` are the amounts the
 * event realized. `StatusReason` says which kind of event it is and therefore
 * which of the fields are meaningful.
 */
export interface Position {
  /**
   * Block/timestamp/tx of this update
   */
  at: BlockTxLogTimestamp;
  /**
   * Market the position belongs to
   */
  mkt: MarketID;
  /**
   * Account the position belongs to
   */
  acc: AccountID;
  /**
   * ID of the position, unique within market
   */
  pid: PositionID;
  /**
   * ID of the corresponding order request
   */
  rq: RequestID;
  /**
   * ID of the corresponding order
   */
  oid: OrderID;
  /**
   * Status of the position
   */
  st: PositionStatus;
  /**
   * Reason for the status of the position
   */
  sr: PositionStatusReason;
  /**
   * Side of the position
   */
  sd: PositionType;
  /**
   * Collateral - the amount locked in the position for a snapshot, or the SIGNED
   * change an event made to it: negative where the event released collateral
   * (`PositionClosed`, `PositionDecreased`, `PositionCollateralDecreased`,
   * `PositionDeleveraged`, `PositionUnwound`) and positive where it took more.
   * `PositionIncreased` and `PositionLiquidated` can go either way.
   * Scaled by `Decimals` of the `CollateralToken` of the `Instance`
   */
  c: Amount;
  /**
   * Entry price, scaled by market `PriceDecimals`
   */
  ep: Price;
  /**
   * Q16 fixed-point fractional residue of `EntryPrice` (PNSQ16).
   * Effective entry price (still scaled by market `PriceDecimals`) can be calculated as:
   * * for LONG position:
   *   = (EntryPrice - 1) + EntryPriceResidue / 65536, if EntryPriceResidue > 0
   *   = EntryPrice                        			, if EntryPriceResidue == 0
   * * for SHORT position:
   *   = EntryPrice + EntryPriceResidue / 65536
   */
  epr?: number /* uint32 */;
  /**
   * Size of the position - current for snapshot or settled for update
   */
  s: Size;
  /**
   * Fees to charge against this position or event as the corresponding "cost"
   * when computing PnL/ROI. This is the total, including any builder fee - a
   * position does not report the builder share separately the way trades and
   * account totals do.
   * For a snapshot: the entry-side fees still outstanding against the position,
   * proportional to its remaining size. Indicative - the amount actually paid was
   * charged when the position was opened or increased, and this is the share of it
   * the remaining size carries. It does NOT include the fee that closing the
   * remaining size will cost, which is not knowable in advance.
   * For an event: everything that event cost in fees - the pro-rata entry fees it
   * settled PLUS `CloseFees`, the fee the close or decrease itself paid. Subtract
   * this from the event's realized PnL and the result is net.
   * Scaled by `Decimals` of the `CollateralToken` of the `Instance`
   */
  fee: Amount;
  /**
   * Fee the close or decrease itself paid, for events that reduce a position.
   * Already included in `Fees`; reported separately for a breakdown of what a
   * trade cost, so the entry share and the exit share can be shown apart.
   * Reported as zero, not omitted, everywhere it does not apply: on a snapshot, on
   * events that add to a position, and on the exits the exchange does not charge -
   * liquidation, deleveraging, unwinding, and closing a frozen account.
   * Always zero against exchange contracts before v1.1.7.5, which charged fees on
   * entry only.
   * Scaled by `Decimals` of the `CollateralToken` of the `Instance`
   */
  cfee?: Amount;
  /**
   * FundingSum at entry block
   */
  efs: SPrice;
  /**
   * Leverage at opening, in hundredths
   */
  lv: number /* uint32 */;
  /**
   * Negative unrealized delta PnL that had to be collateralized to enter at the
   * filled price, i.e. the entry slippage funded up front. Reported by the events
   * that form or add to a position (`PositionOpened`, `PositionIncreased`,
   * `PositionInverted`) and already included in their `Collateral`, which it
   * splits into margin plus this. It is NOT realized PnL - it stays in the
   * position and settles with it.
   * Scaled by `Decimals` of the `CollateralToken` of the `Instance`
   */
  cpnl?: Amount;
  /**
   * Realized delta PnL - aggregated for snapshot or settled by the event.
   * Scaled by `Decimals` of the `CollateralToken` of the `Instance`
   */
  dpnl?: Amount;
  /**
   * Realized premium (funding) PnL - aggregated for snapshot or settled by the event.
   * `PositionIncreased` reports a non-zero amount here: adding to a position
   * restarts its funding period, so the funding accrued so far is paid out or
   * collected in cash at that point and is realized even though nothing closed.
   * Scaled by `Decimals` of the `CollateralToken` of the `Instance`
   */
  fnd?: Amount;
  /**
   * Amount the insurance fund paid to cover a shortfall in settling the position
   * (`InsurancePaymentForSettlement`).
   * Scaled by `Decimals` of the `CollateralToken` of the `Instance`
   */
  pay?: Amount;
  /**
   * Exit price, scaled by market `PriceDecimals`
   */
  xp?: Price;
  /**
   * FundingSum at exit block
   */
  xfs: SPrice;
  /**
   * Block/timestamp/tx the position was opened at
   */
  ots: BlockTxTimestamp;
  /**
   * Recent position events to track in PnL history, from older to newer, filled only in update
   */
  e?: Position[];
}
/**
 * Wallet positions snapshot/update, depending on `MsgType`
 */
export interface WalletPositions extends MessageHeader {
  /**
   * Block/timestamp of the snapshot/update
   */
  at: BlockTimestamp;
  /**
   * Position data, from older to newer
   */
  d: Position[];
}
/**
 * Page of historical position data
 */
export interface PositionHistoryPage {
  /**
   * Position data, from newer to older
   */
  d: Position[];
  /**
   * Next page cursor
   */
  np: string;
}

//////////
// source: wallet.go

export type AccountEventType = number /* uint8 */;
export const AccountEventUnspecified: AccountEventType = 0;
export const AccountEventDeposit: AccountEventType = 1;
export const AccountEventWithdrawal: AccountEventType = 2;
export const AccountEventIncreasePositionCollateral: AccountEventType = 3;
export const AccountEventSettlement: AccountEventType = 4;
export const AccountEventLiquidation: AccountEventType = 5;
export const AccountEventTransferToProtocol: AccountEventType = 6;
export const AccountEventTransferFromProtocol: AccountEventType = 7;
export const AccountEventFunding: AccountEventType = 8;
export const AccountEventDeleveraging: AccountEventType = 9;
export const AccountEventUnwinding: AccountEventType = 10;
export const AccountEventPositionCollateralDecreased: AccountEventType = 11;
export const AccountEventLastForwardedDescIdReset: AccountEventType = 12;
/**
 * Wallet state snapshot/update
 */
export interface Wallet extends MessageHeader {
  /**
   * Block/timestamp of the snapshot/update
   */
  at: BlockTimestamp;
  /**
   * Wallet address
   */
  addr: string;
  /**
   * Current nonce
   */
  n: number /* uint64 */;
  /**
   * Fee level of the wallet
   */
  fl: FeeLevelID;
  /**
   * Wallet accounts, filled only in snapshot (MsgType=MsgTypeWalletSnapshot)
   */
  as?: Account[];
  /**
   * Account statistics, filled only in snapshot (MsgType=MsgTypeWalletSnapshot)
   */
  sts?: AccountStats[];
}
/**
 * Account state snapshot/update.
 * Account snapshot is delivered within the wallet snapshot (MsgType=MsgTypeWalletSnapshot),
 * while updates are independent messages (MsgType=MsgTypeAccountUpdate).
 */
export interface Account extends MessageHeader {
  /**
   * ID of the protocol instance the account belongs to
   */
  in: InstanceID;
  /**
   * ID of the account within the protocol instance
   */
  id: AccountID;
  /**
   * Is account frozen
   */
  fr: boolean;
  /**
   * Account allows order forwarding
   */
  fw: boolean;
  /**
   * Fee tier applied to the account's fills. Indexes the per-tier fee arrays
   * of each market; the first tier is the base rate and higher tiers are
   * progressively discounted.
   */
  ft: number /* uint8 */;
  /**
   * Last known forwarded request id
   */
  lfr: RequestID;
  /**
   * Account balance in collateral token
   */
  b: Amount;
  /**
   * Balance locked in active orders, collateral token
   */
  lb: Amount;
  /**
   * Recent account events, from older to newer, filled only in update
   */
  h?: AccountEvent[];
}
/**
 * Account state modification event
 */
export interface AccountEvent {
  /**
   * Block/timestamp/tx of the event
   */
  at: BlockTxLogTimestamp;
  /**
   * ID of the protocol instance the account belongs to
   */
  in: InstanceID;
  /**
   * ID of the account within the protocol instance
   */
  id: AccountID;
  /**
   * Event type
   */
  et: AccountEventType;
  /**
   * Event context-dependent links with market and/or order
   */
  m?: MarketID;
  r?: OrderID;
  o?: OrderID;
  p?: PositionID;
  /**
   * Account balance change amount, depending on the event type
   * should be added to or substructed from the account balance
   */
  a: Amount;
  /**
   * Updated balance
   */
  b: Amount;
  /**
   * Locked balance
   */
  lb: Amount;
  /**
   * Total fee paid (included into `a`). Includes `bfa`.
   */
  f: Amount;
  /**
   * Builder-fee portion of `f`, in the same units. Zero unless the event came from an
   * order placed with a builder-bound API key that requested a fee.
   */
  bfa?: Amount;
}
/**
 * Page of historical account data
 */
export interface AccountHistoryPage {
  /**
   * Account history data, from newer to older
   */
  d: AccountEvent[];
  /**
   * Next page cursor
   */
  np: string;
}
/**
 * Account statistics snapshot/update.
 * Snapshot is delivered within the wallet snapshot (MsgType=MsgTypeWalletSnapshot),
 * while updates are independent messages (MsgType=MsgTypeAccountStatsUpdate).
 */
export interface AccountStats extends MessageHeader {
  /**
   * ID of the protocol instance the account belongs to
   */
  in: InstanceID;
  /**
   * ID of the account within the protocol instance
   */
  id: AccountID;
  /**
   * Total amount of deposits in collateral token, including credits paid into
   * the account by the protocol (rewards, prizes)
   */
  td: Amount;
  /**
   * Total amount of withdrawals in collateral token, including transfers from
   * the account back to the protocol
   */
  tw: Amount;
  /**
   * Total trading volume
   */
  tv: Amount;
  /**
   * Total fees paid, including `tbf` — the full trading cost
   */
  tf: Amount;
  /**
   * Builder-fee portion of `tf`. Zero for an account that never traded through a
   * builder-bound API key.
   */
  tbf?: Amount;
  /**
   * Total reliazed PnL, net of all fees paid (protocol and builder)
   */
  trp: Amount;
  /**
   * Total win rate, bps
   */
  wr: number /* uint32 */;
  /**
   * Total trades
   */
  tt: number /* uint32 */;
}
/**
 * Wallet portfolio data
 */
export interface Portfolio {
  /**
   * Block/timestamp of the last update
   */
  at: BlockTimestamp;
  /**
   * Chart data points, from older to newer
   */
  chart: ChartPoint[];
}
/**
 * Chart point
 */
export interface ChartPoint {
  /**
   * Timestamp (ms)
   */
  t: number /* uint64 */;
  /**
   * Value (CNS)
   */
  v: Amount;
}
